// GestaoCartoesBaas.js
import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import './GestaoCartoesBaas.css';

/**
 * GestaoCartoesBaas.js
 * -----------------------------------------------------------------------
 * Tela dedicada de CARTÕES do modelo Banking (Possibilidade 1 — Stark Bank).
 * Consome /api/baas/account/{cards,holders,merchant-categories}.
 *
 * A conta em si (saldo, extrato, aporte, boleto, transferência) vive na tela
 * ContaStarkBank — e é ELA que manda no ambiente. Esta tela apenas obedece:
 * lê `account.environment` do GET /account e se adapta.
 *
 * ===========================================================================
 * 🔴 ESCOPO DE AMBIENTE (correção crítica — leia antes de mexer)
 * ===========================================================================
 * Todo dado desta tela pertence a um par (ambiente, Project ID). Sem isso,
 * trocar de sandbox para produção em ContaStarkBank deixava esta tela servindo
 * o snapshot anterior: cartão de teste passando por cartão real.
 *
 * Três regras sustentam a correção:
 *
 *  1. `scopeOf(account)` = "ambiente::projectId". É a identidade do conjunto
 *     de dados. Se ela muda, NADA do escopo anterior sobrevive — cartões,
 *     titulares, catálogo de categorias e endereço de envio são descartados
 *     (`resetScopedCaches`).
 *  2. Nada é hidratado de cache antes do servidor confirmar o escopo. Abro mão
 *     da pintura instantânea de propósito: um segundo mostrando cartão de
 *     sandbox em produção é tempo suficiente para alguém clicar.
 *  3. O ambiente fica VISÍVEL no cabeçalho (selo PRODUÇÃO/SANDBOX). Não se
 *     resolve confusão de ambiente confiando na memória do usuário.
 *
 * O backend faz a parte dele: filtra por `environment` em toda query das
 * tabelas espelho. Aqui é a segunda camada, não a única.
 *
 * ===========================================================================
 * REFINO DE DESEMPENHO
 * ===========================================================================
 *  - Boot em paralelo (Promise.allSettled): custo é o da chamada mais lenta.
 *  - Catálogo de MCC: combobox fechado por padrão (zero nó no DOM até abrir),
 *    carregamento sob demanda com singleton de promise, paginação de 20 em 20,
 *    índice de busca pré-normalizado, Set O(1), linhas memoizadas.
 *  - Lista de cartões: paginação de 20 em 20 com sentinela de rolagem, busca
 *    indexada com debounce, filtro de status em chips, e duas densidades
 *    (blocos — padrão — e linhas).
 *  - Modal em grid de 3 linhas: header e footer fixos, sem rolagem geral.
 *
 * ===========================================================================
 * 🛡️ BLINDAGEM
 * ===========================================================================
 *  - Toda resposta de API é tratada como possivelmente malformada: arrays que
 *    vêm null, campos ausentes, tipos trocados (ver `asArray`, `holderNameOf`).
 *  - Toda mutação valida entrada ANTES de sair, tem guarda de duplo clique, e
 *    confere se o escopo ainda é o mesmo de quando o modal abriu.
 *  - AbortController em todo fetch, cancelado no unmount.
 *  - Dados sensíveis (número/CVV/validade) ficam só em state React: nunca em
 *    storage, nunca em cache de módulo, nunca em console.
 *  - 403 do backend vira mensagem clara em vez de erro cru.
 * -----------------------------------------------------------------------
 */

const STARK_THEME = 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)';
const STARK_UNLOCK_URL = 'https://web.starkbank.com/unlock-card';

// Objeto de estilo estável — evita criar uma nova referência por card/render.
const STARK_THEME_STYLE = Object.freeze({ background: STARK_THEME });

const CARDS_TTL = 60_000;        // janela em que o snapshot do escopo é reaproveitado
const CATEGORY_PAGE = 20;        // itens por página da lista suspensa de MCC
const CARDS_PAGE = 20;           // cartões por lote na grade/lista
const SEARCH_DEBOUNCE = 180;     // ms
const LIMITE_MAX = 1_000_000;    // teto de sanidade pro limite (R$) — espelha o backend

// Densidades de exibição. BLOCOS é o padrão em todo carregamento de página.
const VIEW_BLOCKS = 'blocks';
const VIEW_ROWS = 'rows';

const fmtBRL = (v) => (parseFloat(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const maskCard = (last4) => `•••• •••• •••• ${String(last4 || '').replace(/\D/g, '').slice(-4) || '????'}`;

const INTERVAL_LABEL = { day: 'diário', week: 'semanal', month: 'mensal' };

const STATUS_META = {
    active:   { label: 'ATIVO',     color: '#10b981' },
    blocked:  { label: 'BLOQUEADO', color: '#f59e0b' },
    pending:  { label: 'PENDENTE',  color: '#6366f1' },
    canceled: { label: 'CANCELADO', color: '#94a3b8' },
    expired:  { label: 'EXPIRADO',  color: '#94a3b8' },
};
const statusMeta = (s) => STATUS_META[s] || { label: String(s || '—').toUpperCase(), color: '#94a3b8' };

const STATUS_FILTERS = [
    { id: 'all',      label: 'Todos' },
    { id: 'active',   label: 'Ativos' },
    { id: 'blocked',  label: 'Bloqueados' },
    { id: 'pending',  label: 'Pendentes' },
    { id: 'canceled', label: 'Cancelados' },
    { id: 'expired',  label: 'Expirados' },
];

/** Resposta de API pode vir null/objeto/string quando esperávamos array. */
const asArray = (v) => (Array.isArray(v) ? v : []);

const apiErr = (e, fallback = 'Falha na comunicação com o servidor.') => {
    if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') return null;
    const status = e?.response?.status;
    // O backend recusa por perfil (P_ADMIN nas rotas de escrita). Erro cru aqui
    // não diz nada ao usuário.
    if (status === 403) return 'Seu perfil não tem permissão para esta ação. Fale com um administrador.';
    if (status === 409) return e?.response?.data?.error || 'A conta Stark Bank não está conectada.';
    return e?.response?.data?.error || e?.message || fallback;
};

/** Normaliza texto para busca: minúsculo e sem acentuação. */
const normalize = (s) => String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

/**
 * Plural em português não é concatenação.
 *
 * ⚠️ `cartão` + `ões` = "cartãoões" — foi exatamente o que apareceu na tela
 * quando o titular tinha 0 cartões. Palavras em -ão trocam a terminação, então
 * a única forma segura é escrever as duas formas por extenso.
 */
const plural = (n, singular, pluralForma) => (Math.abs(Number(n)) === 1 ? singular : pluralForma);

const holderNameOf = (c) => String(c?.holder_name || c?.holderName || c?.display_name || '').trim();
const typeLabelOf = (c) => (c?.type === 'physical' ? 'Físico' : 'Virtual');

/**
 * Índice de busca de UM cartão: titular, apelido, 4 últimos dígitos,
 * estabelecimento travado, id da Stark, tipo e status.
 *
 * Os sinônimos ("fisico plastico", "virtual") existem porque o usuário digita
 * o que vê na tela, não o enum da API.
 */
const buildCardSearch = (c) => normalize([
    holderNameOf(c),
    c?.display_name,
    c?.last4,
    c?.merchant_name,
    c?.stark_card_id,
    c?.type === 'physical' ? 'fisico plastico cartao fisico' : 'virtual',
    statusMeta(c?.status).label,
    // Permite procurar "fora da plataforma" / "web banking" direto na busca.
    c?.origem === 'stark' ? 'stark web banking fora da plataforma externo' : 'plataforma blb',
].filter(Boolean).join(' '));

/**
 * Converte a entrada de limite (aceita "1.234,56" ou "1234.56") em número.
 * Retorna null quando inválido — o chamador decide a mensagem.
 * Mesmos limites do backend, para o erro aparecer antes da viagem.
 */
const parseLimite = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return 0;
    const s = String(raw).trim().replace(/\s/g, '');
    const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
    const n = Number(normalized);
    if (!Number.isFinite(n) || n < 0 || n > LIMITE_MAX) return null;
    return Math.round(n * 100) / 100;
};

/** CPF (11) ou CNPJ (14). Vazio é válido: o campo é opcional. */
const taxIdValido = (raw) => {
    const d = String(raw || '').replace(/\D/g, '');
    return d.length === 0 || d.length === 11 || d.length === 14;
};

const ENV_LABEL = { production: 'PRODUÇÃO', sandbox: 'SANDBOX' };
const envLabelOf = (env) => ENV_LABEL[env] || String(env || '').toUpperCase() || '—';

// Tradução das descrições de categoria (MCC) que a Stark Bank retorna.
// Chaveado pelo texto EXATO de `m.name`. Nomes de marca ficam em inglês.
const CATEGORY_PT = {
    'General Services': 'Serviços gerais',
    'Heating, plumbing and A/C': 'Aquecimento, encanamento e ar-condicionado',
    'Electrical services': 'Serviços elétricos',
    'Masonry, stonework and plaster': 'Alvenaria, cantaria e gesso',
    'Carpentry services': 'Serviços de marcenaria',
    'Roofing/Siding, Sheet metal': 'Telhados/revestimentos e chapas metálicas',
    'Concrete work services': 'Serviços de concretagem',
    'Special trade services': 'Serviços especializados',
    'Miscellaneous publishing and printing': 'Editoração e impressão em geral',
    'Typesetting, plate making and related services': 'Composição gráfica e serviços relacionados',
    'Specialty cleaning': 'Limpeza especializada',
    'Railroads': 'Ferrovias',
    'Commuter transport and ferries': 'Transporte urbano e balsas',
    'Passenger railways': 'Ferrovias de passageiros',
    'Ambulance services': 'Serviços de ambulância',
    'Transport by apps, taxis and limousines': 'Transporte por aplicativo, táxi e limusine',
    'Bus lines': 'Linhas de ônibus',
    'Motor freight carriers and trucking (local and long distance), moving and storage companies and local delivery services': 'Transporte rodoviário de cargas, mudanças e entregas',
    'Courier services': 'Serviços de courier / entrega expressa',
    'Public warehousing and storage for farm products, refrigerated goods, household goods and storage': 'Armazenagem pública (agrícola, refrigerada, doméstica)',
    'Cruise lines': 'Cruzeiros marítimos',
    'Boat rentals and leases': 'Aluguel e locação de embarcações',
    'Marinas services and supplies': 'Marinas — serviços e suprimentos',
    'Airlines and air carriers': 'Companhias aéreas',
    'Airports and flying fields': 'Aeroportos e aeródromos',
    'Travel agencies and tour operators': 'Agências de viagem e operadoras de turismo',
    'Tolls and bridge fees': 'Pedágios e tarifas de pontes',
    'Generic transportation services': 'Serviços de transporte em geral',
    'Telecommunication equipment and telephone sales': 'Equipamentos de telecomunicação e telefones',
    'Telecommunication services': 'Serviços de telecomunicação',
    'Computer network services': 'Serviços de rede de computadores',
    'Telegraph services': 'Serviços de telégrafo',
    'Wires and money orders': 'Transferências e ordens de pagamento',
    'Cable, satellite, and other pay television and radio': 'TV a cabo, satélite e rádio por assinatura',
    'Utilities': 'Contas de consumo (água, luz, gás)',
    'Motor vehicle supplies': 'Suprimentos automotivos',
    'Office and commercial furniture': 'Móveis de escritório e comerciais',
    'Generic construction materials': 'Materiais de construção em geral',
    'Photographic, photocopy, microfilm equipment and supplies': 'Equipamentos fotográficos, cópia e microfilme',
    'Computers, peripherals and software': 'Computadores, periféricos e software',
    'Generic commercial equipments': 'Equipamentos comerciais em geral',
    'Medical, dental, ophthalmic, and hospital equipment and supplies': 'Equipamentos médicos, odontológicos e hospitalares',
    'Metal service centers': 'Centros de serviços em metal',
    'Electrical parts and equipment': 'Peças e equipamentos elétricos',
    'Hardware, equipment and supplies': 'Ferragens, equipamentos e suprimentos',
    'Plumbing, heating equipment and supplies': 'Equipamentos de encanamento e aquecimento',
    'Generic industrial supplies': 'Suprimentos industriais em geral',
    'Precious stones and metals, watches and jewelry': 'Pedras e metais preciosos, relógios e joias',
    'Generic durable goods': 'Bens duráveis em geral',
    'Stationary office supplies, printing and writing paper': 'Material de escritório e papelaria',
    'Drugs and druggist sundries': 'Medicamentos e produtos farmacêuticos',
    'Piece goods, notions and other dry goods': 'Tecidos, aviamentos e miudezas',
    'Uniforms and commercial clothing': 'Uniformes e vestuário profissional',
    'Commercial footwear': 'Calçados comerciais',
    'Generic chemicals and allied products': 'Produtos químicos em geral',
    'Petroleum and petroleum products': 'Petróleo e derivados',
    'Books, periodicals and newspapers': 'Livros, revistas e jornais',
    'Florists supplies, nursery stock and flowers': 'Suprimentos para floriculturas e viveiros',
    'Paints, varnishes and supplies': 'Tintas, vernizes e suprimentos',
    'Generic non-durable goods': 'Bens não duráveis em geral',
    'Home supply warehouse stores': 'Materiais para casa (atacado)',
    'Lumber and building materials stores': 'Madeireiras e materiais de construção',
    'Glass, paint and wallpaper stores': 'Vidros, tintas e papel de parede',
    'Hardware stores': 'Lojas de ferragens',
    'Nurseries, lawn and garden supply stores': 'Viveiros e jardinagem',
    'Marketplaces': 'Marketplaces',
    'Mobile home dealers': 'Revendas de casas móveis',
    'Wholesale clubs': 'Clubes de atacado',
    'Duty free stores': 'Lojas duty free',
    'Discount stores': 'Lojas de desconto',
    'Department stores': 'Lojas de departamento',
    'Variety stores': 'Lojas de variedades',
    'Miscellaneous general merchandise': 'Mercadorias em geral',
    'Grocery stores and supermarkets': 'Mercados e supermercados',
    'Freezer and locker meat provisioners': 'Açougues e carnes congeladas',
    'Candy stores': 'Doces e confeitarias',
    'Dairy products stores': 'Laticínios',
    'Bakeries': 'Padarias',
    'Miscellaneous food stores, convenience stores and specialty markets': 'Conveniência e mercados especializados',
    'Car and truck dealers (new & used), service, repairs parts and leasing': 'Concessionárias de carros e caminhões',
    'Car and truck dealers (used only), service, repairs parts and leasing': 'Concessionárias de veículos usados',
    'Auto and home supply stores': 'Autopeças e suprimentos para casa',
    'Automotive tire stores': 'Lojas de pneus',
    'Automotive parts and accessories stores': 'Autopeças e acessórios',
    'Service stations': 'Postos de serviço',
    'Automated fuel dispensers': 'Bombas de combustível automatizadas',
    'Boat dealers': 'Revendas de embarcações',
    'Electric vehicle charging': 'Carregamento de veículos elétricos',
    'Motorcycle shops and dealers': 'Motos — lojas e revendas',
    'Motor homes dealers': 'Revendas de motorhomes',
    'Snowmobile dealers': 'Revendas de moto de neve',
    'Miscellaneous auto dealers': 'Revendas de veículos em geral',
    'Mens and boys clothing and accessories stores': 'Roupas e acessórios masculinos',
    "Womens ready-to-wear stores": 'Roupas femininas prontas',
    "Women's accessory and specialty shops": 'Acessórios e especialidades femininas',
    'Childrens and infants wear stores': 'Roupas infantis',
    'Family clothing stores': 'Roupas para toda a família',
    'Sports and riding apparel stores': 'Roupas esportivas e de equitação',
    'Shoe stores': 'Sapatarias',
    'Furriers and fur shops': 'Peleteria',
    'Clothing stores': 'Lojas de roupas',
    'Tailors and alterations': 'Alfaiates e ajustes',
    'Wig and toupee stores': 'Perucas',
    'Miscellaneous apparel and accessory shops': 'Vestuário e acessórios em geral',
    'Furniture and decorations': 'Móveis e decoração',
    'Floor covering stores': 'Revestimentos de piso',
    'Drapery, window covering and upholstery stores': 'Cortinas, persianas e estofados',
    'Fireplace, fireplace screens and accessories stores': 'Lareiras e acessórios',
    'Miscellaneous home furnishing specialty stores': 'Artigos para casa em geral',
    'Household appliance stores': 'Eletrodomésticos',
    'Guns and ammunition shops': 'Armas e munições',
    'Electronics stores': 'Eletrônicos',
    'Music stores, musical instruments, pianos and sheet music': 'Instrumentos musicais e partituras',
    'Computer software stores': 'Lojas de software',
    'Record stores': 'Lojas de discos',
    'Caterers': 'Buffet e catering',
    'Eating Places and restaurants': 'Restaurantes',
    'Drinking places': 'Bares',
    'Fast food restaurants': 'Fast-food',
    'Digital goods media: Books, movies and music': 'Digital: livros, filmes e música',
    'Digital goods: Games': 'Digital: jogos',
    'Digital goods: Applications (excludes games)': 'Digital: aplicativos',
    'Digital goods: Large digital goods merchant': 'Digital: grandes lojas digitais',
    'Drug stores and pharmacies': 'Farmácias',
    'Package stores, beer, wine, and liquor': 'Bebidas (cerveja, vinho e destilados)',
    'Used merchandise and secondhand stores': 'Brechós e usados',
    'Antique shops': 'Antiquários',
    'Pawn shops': 'Casas de penhor',
    'Wrecking and salvage yards': 'Ferro-velho e sucata',
    'Antique reproductions': 'Reproduções de antiguidades',
    'Bicycle shops': 'Lojas de bicicleta',
    'Sporting goods stores': 'Artigos esportivos',
    'Book stores': 'Livrarias',
    'Stationery stores, office, and school supply stores': 'Papelarias e material escolar',
    'Jewelry, watches, clocks, and silverware stores': 'Joalherias e relojoarias',
    'Hobby, toy and game shops': 'Brinquedos e hobbies',
    'Camera and photographic supply stores': 'Câmeras e suprimentos fotográficos',
    'Gift, card, novelty, and souvenir shops': 'Presentes e lembranças',
    'Luggage and leather goods stores': 'Malas e artigos de couro',
    'Sewing, needlework and fabric stores': 'Aviamentos e tecidos',
    'Glassware and crystal stores': 'Vidros e cristais',
    'Direct marketing: Insurance services': 'Marketing direto: seguros',
    'Direct marketing: Travel': 'Marketing direto: viagens',
    'Door-to-door sales': 'Vendas porta a porta',
    'Direct marketing: Catalog merchant': 'Marketing direto: catálogo',
    'Direct marketing: Combination catalog and retail merchant': 'Marketing direto: catálogo e varejo',
    'Direct marketing: Outbound telemarketing': 'Marketing direto: telemarketing ativo',
    'Direct marketing: Inbound telemarketing': 'Marketing direto: telemarketing receptivo',
    'Direct marketing: Subscription': 'Marketing direto: assinatura',
    'Direct marketing: Other': 'Marketing direto: outros',
    'Artists supply and craft shops': 'Artigos para artistas e artesanato',
    'Art dealers and galleries': 'Galerias de arte',
    'Stamp and coin stores': 'Selos e moedas',
    'Religious goods stores': 'Artigos religiosos',
    'Hearing aids sales and supplies': 'Aparelhos auditivos',
    'Orthopedic goods: Prosthetics': 'Órteses e próteses',
    'Cosmetic stores': 'Cosméticos',
    'Typewriter stores': 'Máquinas de escrever',
    'Non-automotive Fuel dealers': 'Combustíveis (não automotivo)',
    'Florists': 'Floriculturas',
    'Cigar stores': 'Tabacarias',
    'News dealers and newsstands': 'Bancas de jornal',
    'Pet shops, pet food and supplies': 'Pet shops e produtos para animais',
    'Swimming pools sales': 'Piscinas',
    'Electric razor stores': 'Barbeadores elétricos',
    'Tent and awning shops': 'Toldos e tendas',
    'Miscellaneous specialty retail': 'Varejo especializado em geral',
    'Manual cash disburse': 'Saque manual',
    'Automated cash disburse': 'Saque automático',
    'Financial institutions': 'Instituições financeiras',
    'Quasi Cash-Member Financial Institution': 'Quase-dinheiro (instituição financeira)',
    'Non-financial-institution money orders': 'Ordens de pagamento (não bancárias)',
    'Security brokers/dealers': 'Corretoras de valores',
    'Insurance underwriting and premiums': 'Seguros e prêmios',
    'Insurance: Default': 'Seguros (padrão)',
    'Real estate agents and managers: Rentals': 'Imobiliárias — locação',
    'Merchant payment transaction': 'Transação de pagamento a lojista',
    'MoneySend Intracountry': 'Envio de dinheiro (nacional)',
    'MoneySend Intercountry': 'Envio de dinheiro (internacional)',
    'MoneySend Funding': 'Aporte para envio de dinheiro',
    'Funding Transaction (excluding MoneySend)': 'Transação de aporte',
    'Stored value card purchase/load on non-financial institutions': 'Recarga de cartão pré-pago (não bancário)',
    'Network initiated rewards': 'Recompensas da bandeira',
    'Hotels, motels and resorts': 'Hotéis, motéis e resorts',
    'Timeshares': 'Multipropriedade',
    'Sporting and recreation camps': 'Acampamentos esportivos e recreativos',
    'Trailer parks and campgrounds': 'Campings e trailer parks',
    'Laundry and cleaning services': 'Lavanderias e limpeza',
    'Laundries': 'Lavanderias',
    'Dry cleaners': 'Lavagem a seco',
    'Carpet and upholstery cleaning': 'Limpeza de carpetes e estofados',
    'Photographic studios': 'Estúdios fotográficos',
    'Barber and beauty shops': 'Barbearias e salões de beleza',
    'Shoe repair and hat cleaning': 'Conserto de calçados e chapéus',
    'Funeral services and crematories': 'Funerárias e crematórios',
    'Dating and escort services': 'Serviços de acompanhantes',
    'Tax preparation services': 'Serviços de declaração de impostos',
    'Counseling services': 'Serviços de aconselhamento',
    'Buying and shopping services': 'Serviços de compras',
    'Clothing rental': 'Aluguel de roupas',
    'Massage parlors': 'Massagem',
    'Health and beauty spas': 'Spas',
    'Miscellaneous general services': 'Serviços gerais diversos',
    'Advertising services': 'Publicidade',
    'Credit reporting agencies': 'Agências de crédito',
    'Debt collection agencies': 'Agências de cobrança',
    'Commercial photography, art and graphics': 'Fotografia comercial e artes gráficas',
    'Quick copy, repro and blueprint': 'Cópias rápidas e reprografia',
    'Secretarial support services': 'Serviços de secretariado',
    'Exterminating services': 'Dedetização',
    'Cleaning and maintenance': 'Limpeza e manutenção',
    'Employment and temp agencies': 'Agências de emprego',
    'Computer programming': 'Programação',
    'Information retrieval services': 'Recuperação de informação',
    'Computer repairs': 'Conserto de computadores',
    'Consulting and public relations': 'Consultoria e relações públicas',
    'Detective agencies': 'Agências de detetive',
    'Equipment rental': 'Aluguel de equipamentos',
    'Photo developing': 'Revelação de fotos',
    'Miscellaneous business services': 'Serviços empresariais diversos',
    'Veterinary services': 'Serviços veterinários',
    'Truck-stop iteration': 'Postos de parada para caminhões',
    'Car rental agencies': 'Locadoras de veículos',
    'Truck and utility-trailer rentals': 'Aluguel de caminhões e reboques',
    'Recreational vehicle rentals': 'Aluguel de veículos recreativos',
    'Parking lots and garages': 'Estacionamentos',
    'Auto body repair shops': 'Funilaria',
    'Tire retreading and repair': 'Recapagem e conserto de pneus',
    'Auto paint shops': 'Pintura automotiva',
    'Auto service shops': 'Oficinas mecânicas',
    'Car washes': 'Lava-rápido',
    'Towing services': 'Guincho',
    'Electronics repair shops': 'Conserto de eletrônicos',
    'A/C and refrigeration repair': 'Conserto de ar-condicionado e refrigeração',
    'Small appliance repairs': 'Conserto de eletrodomésticos pequenos',
    'Agricultural sooperative': 'Cooperativa agrícola',
    'Watch and jewelry repairs': 'Conserto de relógios e joias',
    'Furniture repair and refinishing': 'Restauração de móveis',
    'Welding repair': 'Solda',
    'Miscellaneous repair shops': 'Oficinas diversas',
    'Landscaping services': 'Paisagismo',
    'Government Owned Lottery': 'Loteria estatal',
    'Government-Licensed Casinos (Online or Internet Gambling)': 'Cassinos licenciados (online)',
    'Government-Licensed Horse/Dog Racing': 'Corridas licenciadas (cavalo/cão)',
    'Picture and video production': 'Produção de imagem e vídeo',
    'Cinemas': 'Cinemas',
    'Video tape rental stores': 'Locadoras de vídeo',
    'Dance hall, studios and schools': 'Salões e escolas de dança',
    'Theatrical ticket agencies': 'Bilheterias de teatro',
    'Bands and orchestras': 'Bandas e orquestras',
    'Billiard and pool establishments': 'Sinuca e bilhar',
    'Bowling alleys': 'Boliche',
    'Sports clubs and fields': 'Clubes e campos esportivos',
    'Tourist attractions and exhibits': 'Atrações turísticas',
    'Public golf courses': 'Campos de golfe públicos',
    'Video amusement game supplies': 'Jogos de fliperama',
    'Video-game arcades': 'Fliperamas',
    'Betting and casino gambling': 'Apostas e cassino',
    'Amusement parks and carnivals': 'Parques de diversão',
    'Country clubs': 'Clubes de campo',
    'Aquariums': 'Aquários',
    'Miscellaneous recreation services': 'Recreação em geral',
    'Doctors': 'Médicos',
    'Dentists and orthodontists': 'Dentistas e ortodontistas',
    'Osteopaths': 'Osteopatas',
    'Chiropractors': 'Quiropraxia',
    'Optometrists and ophthalmologists': 'Oftalmologistas',
    'Opticians and eyeglasses': 'Óticas',
    'Chiropodists e podiatrists': 'Podólogos',
    'Nursing and personal Care': 'Cuidados de enfermagem e pessoais',
    'Hospitals': 'Hospitais',
    'Medical and dental labs': 'Laboratórios médicos e odontológicos',
    'Medical services': 'Serviços médicos',
    'Legal services and attorneys': 'Advocacia',
    'Elementary and secondary schools': 'Ensino fundamental e médio',
    'Colleges and universities': 'Faculdades e universidades',
    'Correspondence schools': 'Ensino por correspondência',
    'Business and secretarial schools': 'Escolas de secretariado',
    'Vocational and trade schools': 'Escolas técnicas',
    'Educational services': 'Serviços educacionais',
    'Child care services': 'Creches',
    'Charitable and social service organizations - fundraising': 'Organizações beneficentes (arrecadação)',
    'Civic, social and fraternal associations': 'Associações civis e sociais',
    'Political organizations': 'Organizações políticas',
    'Religious organizations': 'Organizações religiosas',
    'Automobile associations': 'Associações automobilísticas',
    'Membership organizations': 'Organizações de associados',
    'Testing laboratories': 'Laboratórios de teste',
    'Architectural and surveying services': 'Arquitetura e topografia',
    'Accounting and bookkeeping services': 'Contabilidade',
    'Professional services': 'Serviços profissionais',
    'Court costs, including alimony and child support': 'Custas judiciais (pensão, etc.)',
    'Fines: Government administrative entities': 'Multas governamentais',
    'Bail and bond payments (payment to the surety for the bond, not the actual bond paid to the government agency)': 'Pagamento de fiança',
    'Tax payments: Government agencies': 'Pagamento de impostos',
    'Generic government services': 'Serviços governamentais em geral',
    'Postal services: Government Only': 'Correios',
    'U.S. federal government agencies or departments': 'Órgãos do governo federal dos EUA',
    'Lottery: Government Only': 'Loteria (governo)',
    'Automated Referral Service': 'Serviço automático de referência',
    'Visa Credential Server': 'Servidor de credenciais Visa',
    'GCAS Emergency Services': 'Serviços de emergência GCAS',
    'Gambling - Horse and dog racing, state lottery': 'Jogos de azar (corridas, loteria estadual)',
    'Intra-company purchases': 'Compras intraempresa',
};
const translateCategory = (m) => CATEGORY_PT[m?.name] || m?.name || m?.code;

/**
 * TIPOS de categoria (agrupamentos abrangentes da Stark).
 *
 * ⚠️ Diferente de `code`, que identifica UMA categoria ("serviceStations"), o
 * `type` cobre um GRUPO ("fuel" = tudo relacionado a combustível). Resposta real
 * da Stark para uma regra criada no Web Banking:
 *
 *     "categories": [{ "code": "", "type": "fuel" }]
 *
 * Note o `code` vazio e a AUSÊNCIA de `name`. Quem depende de `name` para
 * rotular simplesmente não vê a restrição — foi o que aconteceu aqui.
 */
const CATEGORY_TYPE_PT = {
    fuel: 'Combustível',
    food: 'Alimentação',
    pets: 'Animais',
    services: 'Serviços',
    retail: 'Varejo',
    transport: 'Transporte',
    travel: 'Viagens',
    health: 'Saúde',
    education: 'Educação',
    entertainment: 'Entretenimento',
    utilities: 'Contas de consumo',
    government: 'Governo',
    financial: 'Financeiro',
    telecom: 'Telecomunicações',
    lodging: 'Hospedagem',
    automotive: 'Automotivo',
    construction: 'Construção',
    industrial: 'Industrial',
    wholesale: 'Atacado',
    clothing: 'Vestuário',
};

/**
 * Rótulo legível de um item de categoria, na ordem do mais específico para o
 * mais genérico. NUNCA devolve vazio: um item sem rótulo conhecido aparece com
 * o próprio identificador, porque "existe uma restrição que não sei nomear" é
 * informação — e "nenhuma restrição" seria mentira.
 */
const rotuloCategoria = (c, mapaCatalogo = null) => {
    if (!c) return null;
    if (c.name) return CATEGORY_PT[c.name] || c.name;

    if (mapaCatalogo) {
        const chave = c.code ? `code:${c.code}` : c.type ? `type:${c.type}` : null;
        const doCatalogo = chave ? mapaCatalogo.get(chave) : null;
        if (doCatalogo?.label) return doCatalogo.label;
    }

    if (c.type && CATEGORY_TYPE_PT[c.type]) return CATEGORY_TYPE_PT[c.type];
    if (c.code) return c.code;
    if (c.type) return c.type;
    if (c.number) return `MCC ${c.number}`;
    return null;
};


// =============================================================================
// CATÁLOGO DE CATEGORIAS — carregamento único por sessão, com índice de busca
// =============================================================================
/**
 * O catálogo da Stark é grande e IMUTÁVEL durante a sessão. Então:
 *  - baixamos no máximo uma vez;
 *  - montamos UMA vez a estrutura já traduzida e já normalizada pra busca;
 *  - requests concorrentes (dois modais montados ao mesmo tempo) reaproveitam
 *    a mesma promise em vôo, em vez de disparar N chamadas idênticas.
 *
 * O estado vive no módulo de propósito: sobrevive a desmontagens de modal,
 * mas morre no reload — não é cache persistente de dado de tenant.
 */
const CATALOG = {
    items: null,    // array já indexado
    promise: null,  // request em vôo (dedupe)
};

/**
 * IDENTIDADE DE CATEGORIA — ponto delicado do contrato com a Stark.
 *
 * `merchantCategory.query` devolve DOIS tipos de item:
 *   - específico:  { code: 'fastFoodRestaurants', number: 5814, name: '...' }
 *   - abrangente:  { type: 'services',            name: '...' }   ← sem `code`
 *
 * E o backend (`buildRule`) espera cada categoria como OBJETO, `{code}` ou
 * `{type}` — nunca string solta e nunca o MCC numérico. Se tratássemos tudo
 * como `code`, todo item do tipo abrangente iria com `code: undefined` e a
 * Stark recusaria a regra (ou pior: aceitaria uma whitelist incompleta,
 * travando o cartão sem ninguém entender por quê).
 *
 * Solução: internamente a seleção é uma string com prefixo ('code:x' /
 * 'type:y') — mantém o Set O(1) e serializa fácil. Só na hora do POST/PATCH
 * ela volta a ser objeto, via `categoriasToPayload`. O caminho inverso
 * (`categoriasFromRule`) aceita o formato legado salvo no banco, onde
 * categoria era string simples e sempre significava `code`.
 */
const catKey = (m) => (m?.code ? `code:${m.code}` : m?.type ? `type:${m.type}` : null);

/** Seleção interna -> payload aceito pelo buildRule do backend. */
const categoriasToPayload = (keys) => (keys || []).map((k) => {
    const idx = String(k).indexOf(':');
    if (idx < 0) return { code: k }; // defensivo: trata como code
    const kind = k.slice(0, idx);
    const value = k.slice(idx + 1);
    return kind === 'type' ? { type: value } : { code: value };
});

/** rules_json do banco (string legada OU objeto) -> seleção interna. */
const categoriasFromRule = (categories) => (categories || [])
    .map((c) => {
        if (typeof c === 'string') return `code:${c}`; // formato legado
        return catKey(c);
    })
    .filter(Boolean);

/** Transforma a resposta crua da Stark no formato otimizado para render/busca. */
function buildCatalogIndex(raw) {
    const list = Array.isArray(raw) ? raw : [];
    const seen = new Set();
    const items = [];

    for (const m of list) {
        const key = catKey(m);
        if (!key || seen.has(key)) continue; // sem identidade utilizável / duplicado
        seen.add(key);
        const label = translateCategory(m);
        items.push({
            key,
            code: m.code || null,
            type: m.type || null,
            number: m.number || null,
            label,
            // Índice de busca: rótulo PT + nome original EN + MCC + identificador.
            // Assim "posto", "service station" e "5541" acham a mesma coisa.
            search: normalize(`${label} ${m.name || ''} ${m.number || ''} ${m.code || ''} ${m.type || ''}`),
        });
    }

    items.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    return items;
}

/**
 * IMPORTANTE: esta request NÃO recebe AbortSignal de chamador.
 * A promise é compartilhada entre todos os comboboxes da sessão — se o
 * primeiro chamador desmontasse e abortasse, ele derrubaria o catálogo de
 * quem estivesse pendurado na MESMA promise. O cancelamento é feito no
 * consumidor (flag `alive`), que simplesmente ignora o resultado.
 */
function loadCategoryCatalog() {
    if (CATALOG.items) return Promise.resolve(CATALOG.items);
    if (CATALOG.promise) return CATALOG.promise;

    CATALOG.promise = api
        .get('/api/baas/account/merchant-categories')
        .then(({ data }) => {
            CATALOG.items = buildCatalogIndex(data);
            CATALOG.promise = null;
            return CATALOG.items;
        })
        .catch((e) => {
            // Não "envenena" o cache: a próxima tentativa pode dar certo.
            CATALOG.promise = null;
            throw e;
        });

    return CATALOG.promise;
}

/** Mapa key -> item, memoizado sobre a identidade do array do catálogo. */
let CATALOG_MAP_REF = null;
let CATALOG_MAP = new Map();
function catalogMap(items) {
    if (CATALOG_MAP_REF !== items) {
        CATALOG_MAP_REF = items;
        CATALOG_MAP = new Map((items || []).map((it) => [it.key, it]));
    }
    return CATALOG_MAP;
}

/**
 * Hook do catálogo. `enabled=false` significa: não toque na rede.
 * É isso que faz o clique no cartão ficar barato — o catálogo só é buscado
 * quando o combobox é realmente aberto (ou quando já há seleção pra rotular).
 */
function useCategoryCatalog(enabled) {
    const [status, setStatus] = useState(() => (CATALOG.items ? 'ready' : 'idle'));
    const [items, setItems] = useState(() => CATALOG.items || []);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!enabled) return undefined;
        if (CATALOG.items) {
            setItems(CATALOG.items);
            setStatus('ready');
            return undefined;
        }

        let alive = true;
        setStatus('loading');

        loadCategoryCatalog()
            .then((list) => {
                if (!alive) return;
                setItems(list);
                setStatus('ready');
            })
            .catch(() => {
                if (!alive) return;
                setStatus('error');
            });

        return () => { alive = false; };
    }, [enabled, attempt]);

    const retry = useCallback(() => setAttempt((a) => a + 1), []);
    return { status, items, retry };
}

// =============================================================================
// HOOKS DE UI (scroll lock, Esc, clique fora, debounce, paginação por scroll)
// =============================================================================
let SCROLL_LOCKS = 0;
function useBodyScrollLock(active) {
    useEffect(() => {
        if (!active) return undefined;
        SCROLL_LOCKS += 1;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            SCROLL_LOCKS = Math.max(0, SCROLL_LOCKS - 1);
            if (SCROLL_LOCKS === 0) document.body.style.overflow = prev || '';
        };
    }, [active]);
}

/** Esc hierárquico: quem estiver mais "por cima" trata e para a propagação. */
function useEscapeKey(handler, active = true) {
    const ref = useRef(handler);
    ref.current = handler;
    useEffect(() => {
        if (!active) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            ref.current?.();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [active]);
}

function useClickOutside(refEl, handler, active = true) {
    const cb = useRef(handler);
    cb.current = handler;
    useEffect(() => {
        if (!active) return undefined;
        const onDown = (e) => {
            const node = refEl.current;
            if (node && !node.contains(e.target)) cb.current?.();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [refEl, active]);
}

function useDebounced(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

/**
 * Paginação incremental por rolagem, reaproveitada pela lista de cartões e
 * pela lista suspensa de categorias.
 *
 * `root = null` observa a viewport (caso da grade de cartões, que rola com a
 * página); passando um elemento, observa a rolagem interna dele (caso do
 * dropdown). Sem IntersectionObserver no navegador, entrega tudo de uma vez —
 * degradar para "lista completa" é melhor que travar a paginação.
 */
function useIncrementalPaging({ enabled, sentinelRef, rootRef = null, total, step, onMore }) {
    useEffect(() => {
        if (!enabled) return undefined;
        const target = sentinelRef.current;
        if (!target) return undefined;

        if (typeof IntersectionObserver === 'undefined') {
            onMore(total);
            return undefined;
        }

        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((en) => en.isIntersecting)) {
                    onMore((v) => Math.min(v + step, total));
                }
            },
            { root: rootRef?.current || null, rootMargin: '200px', threshold: 0 },
        );
        io.observe(target);
        return () => io.disconnect();
    }, [enabled, sentinelRef, rootRef, total, step, onMore]);
}

// =============================================================================
// COMBOBOX DE CATEGORIAS (MCC) — lista suspensa, whitelist, 20 em 20
// =============================================================================

/**
 * Linha da lista. Memoizada: ao digitar na busca ou selecionar um item,
 * só as linhas cujas props mudaram voltam a renderizar.
 */
const CategoryRow = memo(function CategoryRow({ item, checked, onToggle }) {
    return (
        <button
            type="button"
            role="option"
            aria-selected={checked}
            className={`gb-mcc-item ${checked ? 'selected' : ''}`}
            onClick={() => onToggle(item.key)}
        >
            <span className="gb-mcc-item-check" aria-hidden="true">{checked ? '✓' : ''}</span>
            <span className="gb-mcc-item-label">{item.label}</span>
            {item.number && <span className="gb-mcc-item-code">MCC {item.number}</span>}
        </button>
    );
});

function CategoryCombobox({ selected, onChange, disabled = false }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [visible, setVisible] = useState(CATEGORY_PAGE);

    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const sentinelRef = useRef(null);

    // id próprio por instância: dois comboboxes na mesma página não podem
    // compartilhar o mesmo `id`, senão o <label htmlFor> aponta pro errado.
    const triggerId = useRef(`gb-combo-${Math.random().toString(36).slice(2, 9)}`).current;

    // Rede só quando precisa: aberto, ou fechado mas com seleção a rotular.
    const needsCatalog = open || selected.length > 0;
    const { status, items, retry } = useCategoryCatalog(needsCatalog);

    const term = normalize(useDebounced(search, SEARCH_DEBOUNCE));

    // Lookup O(1) — antes era um includes() em array por linha renderizada.
    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const filtered = useMemo(() => {
        if (!term) return items;
        return items.filter((it) => it.search.includes(term));
    }, [items, term]);

    // Só esta fatia vira DOM.
    const page = useMemo(() => filtered.slice(0, visible), [filtered, visible]);
    const hasMore = visible < filtered.length;

    // Chips do que está selecionado (resolvidos pelo mapa do catálogo).
    const selectedItems = useMemo(() => {
        const map = catalogMap(items);
        return selected.map((key) => map.get(key) || { key, label: key.replace(/^(code|type):/, ''), number: null });
    }, [selected, items]);

    // Nova busca reinicia a paginação.
    useEffect(() => { setVisible(CATEGORY_PAGE); }, [term]);

    useIncrementalPaging({
        enabled: open && hasMore,
        sentinelRef,
        rootRef: listRef,
        total: filtered.length,
        step: CATEGORY_PAGE,
        onMore: setVisible,
    });

    // Ao abrir: foca a busca e volta a lista pro topo.
    useEffect(() => {
        if (!open) return;
        setVisible(CATEGORY_PAGE);
        const t = setTimeout(() => inputRef.current?.focus(), 30);
        return () => clearTimeout(t);
    }, [open]);

    useClickOutside(rootRef, () => setOpen(false), open);
    useEscapeKey(() => setOpen(false), open);

    const toggle = useCallback((key) => {
        onChange(selectedSet.has(key)
            ? selected.filter((c) => c !== key)
            : [...selected, key]);
    }, [onChange, selected, selectedSet]);

    const clearAll = useCallback((e) => {
        e?.stopPropagation();
        onChange([]);
    }, [onChange]);

    const triggerLabel = selected.length === 0
        ? 'Qualquer categoria permitida'
        : `${selected.length} categoria${selected.length === 1 ? '' : 's'} permitida${selected.length === 1 ? '' : 's'}`;

    return (
        <div className="gb-combo" ref={rootRef}>
            <div className="gb-combo-labelrow">
                <label className="gb-combo-label" htmlFor={triggerId}>Categorias permitidas</label>
                {selected.length > 0 && (
                    <button type="button" className="gb-mcc-clear" onClick={clearAll}>Limpar seleção</button>
                )}
            </div>

            <button
                id={triggerId}
                type="button"
                className={`gb-combo-trigger ${open ? 'open' : ''} ${selected.length ? 'has-value' : ''}`}
                onClick={() => setOpen((o) => !o)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="gb-combo-trigger-text">{triggerLabel}</span>
                <span className="gb-combo-caret" aria-hidden="true">▾</span>
            </button>

            <p className="gb-combo-hint">
                Vazio = o cartão aceita qualquer categoria. Ao marcar, ele passa a funcionar
                <strong> somente</strong> nas categorias marcadas (a Stark trabalha por lista de permissão).
            </p>

            {selectedItems.length > 0 && (
                <div className="gb-mcc-chips">
                    {selectedItems.slice(0, 8).map((m) => (
                        <button
                            type="button"
                            key={m.key}
                            className="gb-mcc-chip selected"
                            onClick={() => toggle(m.key)}
                            title="Remover esta categoria"
                        >
                            {m.label} <span aria-hidden="true">✕</span>
                        </button>
                    ))}
                    {selectedItems.length > 8 && (
                        <span className="gb-mcc-chip gb-mcc-chip--more">+{selectedItems.length - 8}</span>
                    )}
                </div>
            )}

            {open && (
                <div className="gb-combo-pop" role="dialog" aria-label="Selecionar categorias">
                    <div className="gb-combo-search">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Buscar por nome ou MCC..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoComplete="off"
                            spellCheck="false"
                        />
                        {search && (
                            <button type="button" className="gb-combo-search-clear" onClick={() => setSearch('')} aria-label="Limpar busca">✕</button>
                        )}
                    </div>

                    {status === 'loading' && (
                        <div className="gb-combo-state">
                            <div className="gb-skel-line" />
                            <div className="gb-skel-line" />
                            <div className="gb-skel-line" />
                            <span>Carregando catálogo da Stark...</span>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="gb-combo-state gb-combo-state--error">
                            <span>Não foi possível carregar o catálogo de categorias.</span>
                            <button type="button" className="gb-btn-cancel" onClick={retry}>Tentar de novo</button>
                        </div>
                    )}

                    {status === 'ready' && (
                        <>
                            <div className="gb-combo-list" ref={listRef} role="listbox" aria-multiselectable="true">
                                {filtered.length === 0 ? (
                                    <p className="gb-mcc-empty">Nenhuma categoria encontrada para “{search.trim()}”.</p>
                                ) : (
                                    <>
                                        {page.map((it) => (
                                            <CategoryRow
                                                key={it.key}
                                                item={it}
                                                checked={selectedSet.has(it.key)}
                                                onToggle={toggle}
                                            />
                                        ))}
                                        {hasMore && (
                                            <div className="gb-combo-sentinel" ref={sentinelRef}>
                                                <div className="gb-skel-line" />
                                                <span>Carregando mais...</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="gb-combo-foot">
                                <span>
                                    Exibindo <strong>{page.length}</strong> de <strong>{filtered.length}</strong>
                                    {filtered.length !== items.length ? ` (${items.length} no total)` : ''}
                                </span>
                                <button type="button" className="gb-combo-done" onClick={() => setOpen(false)}>Concluir</button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}



// =============================================================================
// CATÁLOGOS DE REGRA (métodos, propósitos, intervalos, países)
// =============================================================================
/**
 * Catálogo estável durante a sessão, igual ao de categorias: uma requisição por
 * carregamento de página, promise compartilhada para dois modais abertos não
 * dispararem a mesma chamada.
 *
 * Os valores são contrato da Stark (extraídos do fonte do SDK, servidos pelo
 * backend em /rule-catalogs) — não invento rótulo aqui.
 */
const RULE_CATALOGS = { data: null, promise: null };

function loadRuleCatalogs() {
    if (RULE_CATALOGS.data) return Promise.resolve(RULE_CATALOGS.data);
    if (RULE_CATALOGS.promise) return RULE_CATALOGS.promise;
    RULE_CATALOGS.promise = api
        .get('/api/baas/account/rule-catalogs')
        .then(({ data }) => { RULE_CATALOGS.data = data; RULE_CATALOGS.promise = null; return data; })
        .catch((e) => { RULE_CATALOGS.promise = null; throw e; });
    return RULE_CATALOGS.promise;
}

function useRuleCatalogs() {
    const [catalogos, setCatalogos] = useState(() => RULE_CATALOGS.data);
    useEffect(() => {
        if (RULE_CATALOGS.data) return undefined;
        let alive = true;
        loadRuleCatalogs()
            .then((d) => { if (alive) setCatalogos(d); })
            // Falha aqui degrada os seletores, não derruba a tela: o limite e as
            // categorias continuam editáveis.
            .catch(() => { if (alive) setCatalogos({ methods: [], purposes: [], intervals: FALLBACK_INTERVALS, countries: [] }); });
        return () => { alive = false; };
    }, []);
    return { catalogos };
}

// Fallback mínimo caso /rule-catalogs falhe: sem isso o seletor de intervalo
// ficaria vazio e o operador não conseguiria salvar nem o limite.
const FALLBACK_INTERVALS = [
    { code: 'day', label: 'Diário' },
    { code: 'week', label: 'Semanal' },
    { code: 'month', label: 'Mensal' },
];

const INTERVAL_LABEL_FULL = {
    instant: 'por compra',
    day: 'por dia',
    week: 'por semana',
    month: 'por mês',
    year: 'por ano',
    lifetime: 'no total (sem renovar)',
};

const PURPOSE_LABEL = { purchase: 'Compras', withdrawal: 'Saque' };

const METHOD_LABEL = {
    chip: 'Chip',
    contactless: 'Aproximação',
    magstripe: 'Tarja',
    token: 'Carteira digital',
    server: 'Online',
    manual: 'Digitado',
};

/**
 * O que cada forma de pagamento significa e o risco de deixá-la ligada. É só
 * texto de apoio (tooltip + legenda) — NÃO muda nada do que é enviado à Stark.
 * Existe porque "magstripe" e "manual" não dizem nada a quem opera a frota, e
 * são justamente as duas formas que mais convém desligar num cartão corporativo.
 */
const METHOD_HELP = {
    chip: 'Cartão inserido na maquininha (EMV). É o uso presencial mais seguro.',
    contactless: 'Aproximação por NFC (maquininha ou celular). Prático; costuma ter teto por transação.',
    magstripe: 'Tarja magnética. Tecnologia antiga e a mais fácil de clonar — desligar reduz muito o risco.',
    token: 'Carteira digital (Apple Pay, Google Pay). Usa número virtual, não expõe o cartão real.',
    server: 'Compra online / e-commerce, sem o cartão presente. Onde mais ocorre fraude com dados vazados.',
    manual: 'Número digitado à mão pelo lojista. Risco alto: dispensa o cartão físico e o chip.',
};

// =============================================================================
// SELETOR DE CHIPS — métodos e propósitos
// =============================================================================
/**
 * Multi-seleção em chips. Vazio significa "todos permitidos" na Stark, e o
 * componente diz isso explicitamente — a ausência de marcação é fácil de
 * confundir com "nada permitido", que seria o oposto do comportamento real.
 */
/**
 * ⚠️ PREFIXO `gb-pchip`, NÃO `gb-chip` — colisão real, já corrigida uma vez.
 *
 * `.gb-chip` já existe no CSS desta tela: é o retângulo dourado do CHIP do
 * cartão (ver `CardFace` → `<div className="gb-chip" />`), com largura e altura
 * fixas. Ao reaproveitar o nome, todo botão de seleção herdava aquele tamanho e
 * o texto era cortado — "Compras" virava "Con", e o atalho de país aparecia com
 * um quadrado dourado dentro.
 *
 * Nenhum ajuste de flex resolve isso: o conflito é de nome. Se precisar de mais
 * variantes, mantenha o prefixo `gb-pchip` (p de permissão).
 */
function ChipPicker({ opcoes, selecionados, onChange, disabled, vazioLabel, descricoes = null }) {
    const set = useMemo(() => new Set(selecionados), [selecionados]);
    const toggle = (code) => {
        if (disabled) return;
        onChange(set.has(code) ? selecionados.filter((c) => c !== code) : [...selecionados, code]);
    };

    if (!asArray(opcoes).length) {
        return <p className="gb-field-hint">Catálogo indisponível — este filtro não pode ser editado agora.</p>;
    }

    return (
        <>
            <div className="gb-pchips">
                {asArray(opcoes).map((o) => (
                    <button
                        key={o.code}
                        type="button"
                        className={`gb-pchip ${set.has(o.code) ? 'is-on' : ''}`}
                        onClick={() => toggle(o.code)}
                        disabled={disabled}
                        aria-pressed={set.has(o.code)}
                        title={descricoes ? (descricoes[o.code] || undefined) : undefined}
                    >
                        {set.has(o.code) && <span aria-hidden="true">✓ </span>}
                        {o.label || o.code}
                    </button>
                ))}
            </div>
            {selecionados.length === 0 && <p className="gb-field-hint gb-field-hint--neutro">{vazioLabel}</p>}
        </>
    );
}

// =============================================================================
// SELETOR DE PAÍSES
// =============================================================================
/**
 * A Stark usa ISO alpha-3 ("BRA"). O catálogo vem dela, mas o Brasil aparece
 * como atalho porque é 99% dos casos de uma frota nacional.
 */
function PaisPicker({ opcoes, selecionados, onChange, disabled }) {
    const [busca, setBusca] = useState('');
    const set = useMemo(() => new Set(selecionados), [selecionados]);

    const lista = useMemo(() => {
        const termo = normalize(busca);
        const base = asArray(opcoes);
        if (!termo) return base.slice(0, 12);
        return base.filter((c) => normalize(`${c.name} ${c.code} ${c.shortCode}`).includes(termo)).slice(0, 12);
    }, [opcoes, busca]);

    const toggle = (code) => {
        if (disabled) return;
        onChange(set.has(code) ? selecionados.filter((c) => c !== code) : [...selecionados, code]);
    };

    return (
        <>
            <p className="gb-field-hint">
                Vazio = qualquer país. Restringir ao Brasil impede uso no exterior e em site estrangeiro.
            </p>

            {selecionados.length > 0 && (
                <div className="gb-pchips">
                    {selecionados.map((code) => {
                        const p = asArray(opcoes).find((x) => x.code === code);
                        return (
                            <button
                                key={code}
                                type="button"
                                className="gb-pchip is-on"
                                onClick={() => toggle(code)}
                                disabled={disabled}
                                title="Remover"
                            >
                                {p?.name || code} <span aria-hidden="true">✕</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {!set.has('BRA') && (
                <button type="button" className="gb-pchip gb-pchip--sugestao" onClick={() => toggle('BRA')} disabled={disabled}>
                    + Restringir ao Brasil
                </button>
            )}

            <input
                type="search"
                className="gb-pais-busca"
                placeholder="Buscar país..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                disabled={disabled}
            />

            {busca && (
                <div className="gb-pchips">
                    {lista.length === 0 ? (
                        <p className="gb-field-hint">Nenhum país encontrado.</p>
                    ) : lista.filter((p) => !set.has(p.code)).map((p) => (
                        <button key={p.code} type="button" className="gb-pchip" onClick={() => toggle(p.code)} disabled={disabled}>
                            {p.name} <small>{p.code}</small>
                        </button>
                    ))}
                </div>
            )}
        </>
    );
}

// =============================================================================
// JANELA DE HORÁRIO
// =============================================================================
/**
 * A Stark aceita a janela como FRASE em inglês:
 *   "every monday, wednesday from 00:00 to 23:59 in America/Sao_Paulo"
 *
 * Pedir isso digitado seria convite a erro de sintaxe silencioso, então o
 * componente monta a frase a partir de dias + horário, e mostra o resultado. O
 * campo cru fica visível para quem precisa colar uma expressão vinda da Stark.
 */
const DIAS_SEMANA = [
    { code: 'monday', label: 'Seg' },
    { code: 'tuesday', label: 'Ter' },
    { code: 'wednesday', label: 'Qua' },
    { code: 'thursday', label: 'Qui' },
    { code: 'friday', label: 'Sex' },
    { code: 'saturday', label: 'Sáb' },
    { code: 'sunday', label: 'Dom' },
];

const TZ_PADRAO = 'America/Sao_Paulo';

function ScheduleBuilder({ value, onChange, disabled }) {
    // Interpreta a frase existente para pré-marcar os controles. Tolerante: se
    // não reconhecer o formato, mantém o texto e deixa o operador editar cru.
    const parsed = useMemo(() => {
        const texto = String(value || '');
        if (!texto.trim()) return { dias: [], de: '', ate: '' };
        const dias = DIAS_SEMANA.filter((d) => new RegExp(`\\b${d.code}\\b`, 'i').test(texto)).map((d) => d.code);
        const horas = texto.match(/from\s+(\d{2}:\d{2})\s+to\s+(\d{2}:\d{2})/i);
        return { dias, de: horas?.[1] || '', ate: horas?.[2] || '' };
    }, [value]);

    const [dias, setDias] = useState(parsed.dias);
    const [de, setDe] = useState(parsed.de);
    const [ate, setAte] = useState(parsed.ate);
    const montouRef = useRef(false);

    // Só propaga depois da primeira interação: sem isso, abrir o modal marcaria
    // o formulário como "alterado" sem ninguém ter tocado em nada.
    useEffect(() => {
        if (!montouRef.current) { montouRef.current = true; return; }
        if (dias.length === 0 && !de && !ate) { onChange(''); return; }
        if (!de || !ate) return;
        const listaDias = dias.length ? dias.join(', ') : DIAS_SEMANA.map((d) => d.code).join(', ');
        onChange(`every ${listaDias} from ${de} to ${ate} in ${TZ_PADRAO}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dias, de, ate]);

    const toggleDia = (code) => {
        if (disabled) return;
        setDias((prev) => (prev.includes(code) ? prev.filter((d) => d !== code) : [...prev, code]));
    };

    const limpar = () => { setDias([]); setDe(''); setAte(''); onChange(''); };

    return (
        <>
            <p className="gb-field-hint">
                Vazio = qualquer dia e horário. Útil para impedir uso fora do turno.
            </p>

            <div className="gb-pchips">
                {DIAS_SEMANA.map((d) => (
                    <button
                        key={d.code}
                        type="button"
                        className={`gb-pchip gb-pchip--dia ${dias.includes(d.code) ? 'is-on' : ''}`}
                        onClick={() => toggleDia(d.code)}
                        disabled={disabled}
                        aria-pressed={dias.includes(d.code)}
                    >
                        {d.label}
                    </button>
                ))}
            </div>

            <div className="gb-form-row">
                <div className="gb-form-field">
                    <label htmlFor="gb-sched-de">Das</label>
                    <input id="gb-sched-de" type="time" value={de} onChange={(e) => setDe(e.target.value)} disabled={disabled} />
                </div>
                <div className="gb-form-field">
                    <label htmlFor="gb-sched-ate">Até</label>
                    <input id="gb-sched-ate" type="time" value={ate} onChange={(e) => setAte(e.target.value)} disabled={disabled} />
                </div>
            </div>

            {value ? (
                <div className="gb-sched-preview">
                    <small>Enviado à Stark ({TZ_PADRAO})</small>
                    <code>{value}</code>
                    <button type="button" onClick={limpar} disabled={disabled}>Remover janela</button>
                </div>
            ) : (
                (dias.length > 0 && (!de || !ate)) && (
                    <p className="gb-field-hint gb-field-hint--aviso">
                        Escolha o horário inicial e final para a janela valer.
                    </p>
                )
            )}
        </>
    );
}

// =============================================================================
// CACHE DA LISTA DE CARTÕES — com ESCOPO DE AMBIENTE
// =============================================================================
/**
 * ⚠️ `scope` não é enfeite: é o que impede dado de sandbox de aparecer em
 * produção. Todo conteúdo desta tela pertence a um par (ambiente, Project ID).
 * Se o par muda, o conteúdo anterior deixa de ser válido — inteiro.
 */
const CARDS_CACHE = { scope: null, connected: false, cards: [], holders: [], at: 0 };

const scopeOf = (account) => (account
    ? `${account.environment || '?'}::${account.stark_project_id || '?'}`
    : null);

const cacheIsFresh = (scope) => (
    !!scope
    && CARDS_CACHE.scope === scope
    && CARDS_CACHE.at > 0
    && (Date.now() - CARDS_CACHE.at) < CARDS_TTL
);

const invalidateCardsCache = () => { CARDS_CACHE.at = 0; };

// Endereço padrão do workspace: imutável dentro de um escopo, não entre escopos.
const ADDR_CACHE = { data: null, promise: null };
function loadWorkspaceAddress({ signal } = {}) {
    if (ADDR_CACHE.data) return Promise.resolve(ADDR_CACHE.data);
    if (ADDR_CACHE.promise) return ADDR_CACHE.promise;
    ADDR_CACHE.promise = api
        .get('/api/baas/account/workspace-address', { signal })
        .then(({ data }) => { ADDR_CACHE.data = data || { available: false }; ADDR_CACHE.promise = null; return ADDR_CACHE.data; })
        .catch((e) => { ADDR_CACHE.promise = null; throw e; });
    return ADDR_CACHE.promise;
}

/**
 * Troca de ambiente ou de Project: nada do escopo anterior sobrevive.
 *
 * O catálogo de categorias e o endereço de envio também são por ambiente — o
 * catálogo vem da Stark daquele Project, o endereço vem de um cartão daquele
 * Workspace. Deixar qualquer um dos dois para trás recria o bug numa forma
 * mais sutil.
 */
const resetScopedCaches = () => {
    CARDS_CACHE.scope = null;
    CARDS_CACHE.connected = false;
    CARDS_CACHE.cards = [];
    CARDS_CACHE.holders = [];
    CARDS_CACHE.at = 0;
    CATALOG.items = null;
    CATALOG.promise = null;
    CATALOG_MAP_REF = null;
    CATALOG_MAP = new Map();
    ADDR_CACHE.data = null;
    ADDR_CACHE.promise = null;
};

/**
 * Guarda de escopo para mutações. Cobre a janela em que um modal fica aberto
 * enquanto o ambiente muda em outra aba: salvar ali aplicaria a alteração no
 * ambiente errado (ou falharia de forma confusa na Stark).
 */
const escopoAindaVale = (scopeAoAbrir) => {
    if (!scopeAoAbrir || !CARDS_CACHE.scope) return true;
    return CARDS_CACHE.scope === scopeAoAbrir;
};

const avisarEscopoMudou = () => {
    toast.error('O ambiente da conta Stark Bank mudou. Recarregue a tela antes de continuar.', { toastId: 'gb-scope' });
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export default function GestaoCartoesBaas() {
    // ⚠️ Nada é hidratado de cache antes do servidor confirmar QUAL ambiente
    // está ativo. Skeleton por um instante é infinitamente melhor que exibir
    // cartão de sandbox numa conta de produção.
    const [hydrated, setHydrated] = useState(false);
    const [revalidating, setRevalidating] = useState(false);
    const [connected, setConnected] = useState(false);
    const [account, setAccount] = useState(null);
    const [cards, setCards] = useState([]);
    const [holders, setHolders] = useState([]);
    const [cardsSource, setCardsSource] = useState(null);   // 'stark' | 'mirror'
    const [cardsTruncated, setCardsTruncated] = useState(false);
    const [cardsError, setCardsError] = useState(null);   // { status, message } quando /cards cai por egress/IP

    const [selected, setSelected] = useState(null);
    const [showIssue, setShowIssue] = useState(false);
    const [sensitiveReveal, setSensitiveReveal] = useState(null);
    const [showDiag, setShowDiag] = useState(false);
    const [showTitulares, setShowTitulares] = useState(false);

    // ── Estado de exploração da lista ────────────────────────────────────────
    // `view` NÃO é persistido de propósito: a tela sempre abre em BLOCOS.
    const [view, setView] = useState(VIEW_BLOCKS);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [visibleCount, setVisibleCount] = useState(CARDS_PAGE);

    const listSentinelRef = useRef(null);
    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const scope = scopeOf(account);

    /**
     * Busca tudo em PARALELO — o custo é o da chamada mais lenta, não a soma.
     * `allSettled` porque cards/holders falham legitimamente quando a conta não
     * está conectada (409), e isso não deve derrubar a tela.
     */
    const fetchAll = useCallback(async (signal) => {
        const [accRes, cardsRes, holdersRes] = await Promise.allSettled([
            api.get('/api/baas/account', { signal }),
            api.get('/api/baas/account/cards', { signal }),
            api.get('/api/baas/account/holders', { signal }),
        ]);

        // O /account é o único indispensável: é ele que define o ambiente.
        if (accRes.status === 'rejected') throw accRes.reason;

        const nextAccount = accRes.value?.data?.account || null;
        const isConnected = !!accRes.value?.data?.connected;

        const nextCards = isConnected && cardsRes.status === 'fulfilled'
            ? asArray(cardsRes.value?.data) : [];
        const nextHolders = isConnected && holdersRes.status === 'fulfilled'
            ? asArray(holdersRes.value?.data) : [];

        // Boot: se /cards falhou POR EGRESS/IP (503/502 ou mensagem clara), a
        // lista vazia não significa "sem cartões" — significa "não consegui
        // perguntar". `allSettled` engole o motivo, então o recuperamos aqui.
        // Abort não conta (troca de escopo/unmount), e 409 é "não conectado",
        // já tratado por isConnected.
        let nextCardsError = null;
        if (isConnected && cardsRes.status === 'rejected') {
            const ce = cardsRes.reason;
            const st = ce?.response?.status;
            const emsg = String(ce?.response?.data?.error || ce?.message || '');
            const aborted = ce?.code === 'ERR_CANCELED' || ce?.name === 'CanceledError' || ce?.name === 'AbortError';
            if (!aborted && (st === 502 || st === 503 || /IP de sa|allowlist|egress|proxy|invalidIp/i.test(emsg))) {
                nextCardsError = { status: st || null, message: emsg || 'Falha ao carregar os cartões.' };
            }
        }

        // O backend informa se a lista veio da Stark ou do espelho local (quando
        // a Stark não respondeu). Isso precisa aparecer na tela: uma cópia local
        // pode estar desatualizada, e quem opera tem que saber disso.
        const headers = cardsRes.status === 'fulfilled' ? (cardsRes.value?.headers || {}) : {};
        const readHeader = (k) => (typeof headers.get === 'function' ? headers.get(k) : headers[k]);
        const nextSource = String(readHeader('x-baas-cards-source') || '').toLowerCase() || null;
        const nextTruncated = !!readHeader('x-baas-cards-truncated');

        return { isConnected, nextAccount, nextCards, nextHolders, nextSource, nextTruncated, nextCardsError };
    }, []);

    // Controller da requisição em vôo — abortado no unmount e a cada nova carga,
    // pra não deixar request órfã nem resposta atrasada sobrescrevendo estado
    // mais novo.
    const inFlightRef = useRef(null);

    const load = useCallback(async ({ background = false } = {}) => {
        inFlightRef.current?.abort();
        const controller = new AbortController();
        inFlightRef.current = controller;
        if (background) setRevalidating(true);

        try {
            const { isConnected, nextAccount, nextCards, nextHolders, nextSource, nextTruncated, nextCardsError } = await fetchAll(controller.signal);
            if (!aliveRef.current) return;

            // 🔴 O ponto que corrige o vazamento: se o escopo confirmado difere
            // do que está em memória, o catálogo e o endereço em cache são de
            // outro ambiente. Descartar TUDO é a única resposta segura.
            const nextScope = scopeOf(nextAccount);
            if (CARDS_CACHE.scope && CARDS_CACHE.scope !== nextScope) {
                resetScopedCaches();
                // Filtros de tela também perdem sentido entre ambientes.
                setSearch('');
                setStatusFilter('all');
                setVisibleCount(CARDS_PAGE);
                setSelected(null);
                setShowIssue(false);
                setCardsError(null);
            }

            setAccount(nextAccount);
            setConnected(isConnected);
            setCards(nextCards);
            setHolders(nextHolders);
            setCardsSource(nextSource);
            setCardsTruncated(nextTruncated);
            setCardsError(nextCardsError);

            CARDS_CACHE.scope = nextScope;
            CARDS_CACHE.connected = isConnected;
            CARDS_CACHE.cards = nextCards;
            CARDS_CACHE.holders = nextHolders;
            CARDS_CACHE.at = Date.now();
        } catch (e) {
            const aborted = e?.name === 'CanceledError' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED';
            if (!aborted && aliveRef.current) {
                const msg = apiErr(e);
                if (msg) toast.error(msg);
            }
        } finally {
            if (inFlightRef.current === controller) inFlightRef.current = null;
            if (aliveRef.current && !controller.signal.aborted) {
                setHydrated(true);
                setRevalidating(false);
            }
        }
    }, [fetchAll]);

    useEffect(() => {
        load({ background: cacheIsFresh(CARDS_CACHE.scope) });
        return () => { inFlightRef.current?.abort(); };
    }, [load]);

    const refresh = useCallback(() => {
        invalidateCardsCache();
        load({ background: true });
    }, [load]);

    const handleCreated = useCallback((result) => {
        setShowIssue(false);
        if (result?.sensitive) {
            // Só em state React: nunca em storage, nunca em cache de módulo.
            setSensitiveReveal({
                ...result.sensitive,
                holderName: result.holderName,
                type: result.type,
                last4: result.last4,
            });
        }
        refresh();
    }, [refresh]);

    // ── BUSCA E FILTRO ───────────────────────────────────────────────────────
    // O índice de cada cartão é montado UMA vez por lista, não por tecla
    // digitada. Assim filtrar é só um `includes` numa string curta.
    const indexed = useMemo(
        () => asArray(cards).map((card) => ({ card, search: buildCardSearch(card) })),
        [cards],
    );

    // Quantos cartões nasceram fora da plataforma. O operador precisa ver isso
    // de relance: é a diferença entre "a tela está errada" e "alguém emitiu no
    // Web Banking".
    const externosCount = useMemo(
        () => indexed.reduce((acc, i) => acc + (i.card?.origem === 'stark' ? 1 : 0), 0),
        [indexed],
    );

    const statusCounts = useMemo(() => {
        const acc = { all: indexed.length };
        for (const { card } of indexed) {
            const k = card?.status || 'desconhecido';
            acc[k] = (acc[k] || 0) + 1;
        }
        return acc;
    }, [indexed]);

    const term = normalize(useDebounced(search, SEARCH_DEBOUNCE));

    const filteredCards = useMemo(() => {
        let out = indexed;
        if (statusFilter !== 'all') out = out.filter((i) => i.card?.status === statusFilter);
        if (term) out = out.filter((i) => i.search.includes(term));
        return out.map((i) => i.card);
    }, [indexed, statusFilter, term]);

    // ── PAGINAÇÃO DE 20 EM 20 ────────────────────────────────────────────────
    // Só esta fatia vira DOM. Cada face de cartão tem gradiente, pseudo-
    // elementos e sombra: o custo não é só de nós, é de pintura.
    const visibleCards = useMemo(
        () => filteredCards.slice(0, visibleCount),
        [filteredCards, visibleCount],
    );
    const hasMoreCards = visibleCount < filteredCards.length;

    // Trocar busca/filtro/densidade volta pro primeiro lote. Um refresh em
    // background NÃO reseta — seria rude puxar o usuário de volta ao topo.
    useEffect(() => { setVisibleCount(CARDS_PAGE); }, [term, statusFilter, view]);

    useIncrementalPaging({
        enabled: hasMoreCards,
        sentinelRef: listSentinelRef,
        total: filteredCards.length,
        step: CARDS_PAGE,
        onMore: setVisibleCount,
    });

    const loadMore = useCallback(() => {
        setVisibleCount((v) => Math.min(v + CARDS_PAGE, filteredCards.length));
    }, [filteredCards.length]);

    const clearFilters = useCallback(() => {
        setSearch('');
        setStatusFilter('all');
    }, []);

    const filtrando = !!term || statusFilter !== 'all';
    // Uma única fonte para o ambiente dentro deste componente — antes cada
    // ponto lia `account?.environment` na mão, e o modal de diagnóstico acabou
    // recebendo uma variável que não existia neste escopo.
    const environment = account?.environment || null;
    const isProd = environment === 'production';

    const anyModalOpen = !!selected || showIssue || !!sensitiveReveal || showDiag || showTitulares;
    useBodyScrollLock(anyModalOpen);

    const subtitle = !hydrated
        ? 'Confirmando ambiente da conta...'
        : connected
            ? `${cards.length} ${cards.length === 1 ? 'cartão emitido' : 'cartões emitidos'} · Stark Bank · ${envLabelOf(environment)}`
            : 'Emissão e gestão de cartões da conta Stark Bank conectada.';

    return (
        <div className="gb-container">
            <div className="gb-header">
                <div>
                    <h2>
                        Cartões Corporativos
                        {/* Selo de ambiente: a confusão sandbox/produção não pode
                            depender da memória de quem está usando a tela. */}
                        {hydrated && environment && (
                            <span className={`gb-env-badge ${isProd ? 'is-prod' : 'is-sbx'}`}>
                                ● {envLabelOf(environment)}
                            </span>
                        )}
                    </h2>
                    <p>
                        {subtitle}
                        {revalidating && hydrated && <span className="gb-revalidating"> · atualizando</span>}
                    </p>
                </div>
                {connected && (
                    <div className="gb-header-actions">
                        <button className="gb-btn-sync" onClick={refresh} disabled={revalidating}>
                            {revalidating ? '⟳ Atualizando...' : '⟳ Atualizar'}
                        </button>
                        <button
                            className="gb-btn-sync"
                            onClick={() => setShowDiag(true)}
                            title="Descobrir por que um cartão da Stark não aparece aqui"
                        >
                            🔍 Diagnóstico
                        </button>
                        <button
                            className="gb-btn-sync"
                            onClick={() => setShowTitulares(true)}
                            title="Controle de gastos por titular — vale para todos os cartões dele"
                        >
                            👤 Titulares
                        </button>
                        <button className="gb-btn-new" onClick={() => setShowIssue(true)}>+ Emitir cartão</button>
                    </div>
                )}
            </div>

            {/* Aviso permanente em sandbox: evita emitir de teste achando que é
                real, e evita o susto inverso de não achar os cartões. */}
            {hydrated && connected && !isProd && (
                <div className="gb-env-notice">
                    <strong>Ambiente de testes (Sandbox).</strong>
                    <span> Os cartões abaixo são de teste e não existem em produção. Para operar de verdade, troque o ambiente na tela "Conta Digital Stark Bank".</span>
                </div>
            )}

            {/* A Stark não respondeu: a lista é cópia local, possivelmente
                desatualizada. Silenciar isso seria mentir por omissão. */}
            {hydrated && connected && cardsSource === 'mirror' && (
                <div className="gb-stale-notice">
                    <strong>Exibindo cópia local.</strong>
                    <span> A Stark Bank não respondeu agora, então esta lista pode estar desatualizada e cartões emitidos fora da plataforma podem não aparecer.</span>
                    <button type="button" onClick={refresh} disabled={revalidating}>Tentar de novo</button>
                </div>
            )}

            {hydrated && connected && cardsTruncated && (
                <div className="gb-stale-notice">
                    <strong>Lista parcial.</strong>
                    <span> Esta conta tem mais cartões do que o limite de uma consulta. Use a busca para encontrar um cartão específico.</span>
                </div>
            )}

            {/* Boot falhou por rota de saída (egress/invalidIp): a lista veio
                vazia não porque não há cartões, mas porque a chamada à Stark foi
                barrada. Silenciar isso levaria alguém a reemitir cartões que já
                existem. O caminho de conserto é o Diagnóstico. */}
            {hydrated && connected && cardsError && (
                <div className="gb-stale-notice gb-stale-notice--erro">
                    <strong>Não foi possível carregar os cartões.</strong>
                    <span>
                        {cardsError.status === 503
                            ? ' A integração está indisponível por configuração de rede (IP fixo de saída).'
                            : cardsError.status === 502
                                ? ' A Stark Bank recusou a chamada por IP de saída fora da allowlist.'
                                : ' A comunicação com a Stark Bank falhou.'}
                        {' '}Os cartões podem existir — o que falhou foi a consulta.
                    </span>
                    <button type="button" onClick={() => setShowDiag(true)}>Ver diagnóstico</button>
                    <button type="button" onClick={refresh} disabled={revalidating}>Tentar de novo</button>
                </div>
            )}

            {/* ── Barra de exploração: busca + status + densidade ──────────── */}
            {hydrated && connected && cards.length > 0 && (
                <div className="gb-toolbar">
                    <div className="gb-toolbar-search">
                        <span className="gb-toolbar-search-icon" aria-hidden="true">🔍</span>
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value.slice(0, 80))}
                            placeholder="Buscar por titular, 4 últimos dígitos, estabelecimento..."
                            aria-label="Buscar cartões"
                            autoComplete="off"
                            spellCheck="false"
                        />
                        {search && (
                            <button
                                type="button"
                                className="gb-toolbar-search-clear"
                                onClick={() => setSearch('')}
                                aria-label="Limpar busca"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    <div className="gb-toolbar-filters" role="group" aria-label="Filtrar por status">
                        {STATUS_FILTERS.map((f) => {
                            const n = statusCounts[f.id] || 0;
                            // Esconde status que não existem na frota — chip zerado é ruído.
                            if (f.id !== 'all' && n === 0) return null;
                            return (
                                <button
                                    key={f.id}
                                    type="button"
                                    className={`gb-filter-chip ${statusFilter === f.id ? 'active' : ''}`}
                                    onClick={() => setStatusFilter(f.id)}
                                    aria-pressed={statusFilter === f.id}
                                >
                                    {f.label}
                                    <span className="gb-filter-chip-count">{n}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="gb-view-toggle" role="group" aria-label="Densidade de exibição">
                        <button
                            type="button"
                            className={`gb-view-btn ${view === VIEW_BLOCKS ? 'active' : ''}`}
                            onClick={() => setView(VIEW_BLOCKS)}
                            aria-pressed={view === VIEW_BLOCKS}
                            title="Exibir em blocos"
                        >
                            <span aria-hidden="true">▦</span> Blocos
                        </button>
                        <button
                            type="button"
                            className={`gb-view-btn ${view === VIEW_ROWS ? 'active' : ''}`}
                            onClick={() => setView(VIEW_ROWS)}
                            aria-pressed={view === VIEW_ROWS}
                            title="Exibir em linhas"
                        >
                            <span aria-hidden="true">☰</span> Linhas
                        </button>
                    </div>
                </div>
            )}

            {!hydrated ? (
                <div className="gb-cards-grid" aria-busy="true">
                    {[0, 1, 2].map((i) => <div key={i} className="gb-card gb-card--skeleton" />)}
                </div>
            ) : !connected ? (
                <div className="gb-holder-notice">
                    <div>
                        <strong>Nenhuma conta Stark Bank conectada.</strong>
                        <span> Conecte a conta na tela "Conta Digital Stark Bank" antes de emitir cartões.</span>
                    </div>
                </div>
            ) : cardsError && cards.length === 0 ? (
                // NÃO dizer "nenhum cartão emitido" aqui: a lista está vazia
                // porque a consulta à Stark falhou, não porque não há cartões.
                // Afirmar o contrário levaria alguém a reemitir o que já existe.
                <div className="gb-empty">
                    <span>⚠️</span>
                    <p>Não foi possível carregar os cartões desta conta agora.</p>
                    <button onClick={() => setShowDiag(true)}>Abrir diagnóstico</button>
                </div>
            ) : cards.length === 0 ? (
                <div className="gb-empty">
                    <span>💳</span>
                    <p>
                        Nenhum cartão emitido ainda nesta conta
                        <strong> ({envLabelOf(environment)})</strong>.
                    </p>
                    <button onClick={() => setShowIssue(true)}>+ Emitir o primeiro cartão</button>
                </div>
            ) : filteredCards.length === 0 ? (
                // Nada encontrado é diferente de "não existe nada" — e a saída
                // tem que ser óbvia, senão o usuário acha que perdeu os cartões.
                <div className="gb-empty">
                    <span>🔍</span>
                    <p>
                        Nenhum cartão encontrado
                        {term ? <> para <strong>“{search.trim()}”</strong></> : null}
                        {statusFilter !== 'all' ? ' com esse status' : ''}.
                    </p>
                    <button onClick={clearFilters}>Limpar filtros</button>
                </div>
            ) : (
                <>
                    {view === VIEW_BLOCKS ? (
                        <div className="gb-cards-grid">
                            {visibleCards.map((c) => (
                                <CardFace key={c.stark_card_id} card={c} onSelect={setSelected} />
                            ))}
                        </div>
                    ) : (
                        <div className="gb-rows" role="table" aria-label="Cartões emitidos">
                            <div className="gb-rows-head" role="row">
                                <span role="columnheader">Titular</span>
                                <span role="columnheader">Cartão</span>
                                <span role="columnheader">Tipo</span>
                                <span role="columnheader">Limite</span>
                                <span role="columnheader">Status</span>
                            </div>
                            {visibleCards.map((c) => (
                                <CardRow key={c.stark_card_id} card={c} onSelect={setSelected} />
                            ))}
                        </div>
                    )}

                    {/* Rodapé: contador + sentinela de rolagem + botão. O botão
                        não é redundante — é o caminho por teclado e o fallback
                        de navegador sem IntersectionObserver. */}
                    <div className="gb-list-foot">
                        <span className="gb-list-count">
                            Exibindo <strong>{visibleCards.length}</strong> de <strong>{filteredCards.length}</strong>
                            {filtrando && filteredCards.length !== cards.length ? ` (${cards.length} no total)` : ''}
                            {cardsSource === 'stark' ? ' · espelhado da Stark Bank' : ''}
                            {externosCount > 0 ? ` · ${externosCount} emitido${externosCount === 1 ? '' : 's'} fora da plataforma` : ''}
                        </span>

                        {hasMoreCards && (
                            <div className="gb-list-more" ref={listSentinelRef}>
                                <button type="button" className="gb-btn-loadmore" onClick={loadMore}>
                                    Carregar mais {Math.min(CARDS_PAGE, filteredCards.length - visibleCount)}
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {showIssue && (
                <IssueCardModal
                    holders={holders}
                    scope={scope}
                    environment={environment}
                    onClose={() => setShowIssue(false)}
                    onCreated={handleCreated}
                />
            )}

            {selected && (
                <CardDetailModal
                    card={selected}
                    scope={scope}
                    environment={environment}
                    onClose={() => setSelected(null)}
                    onChanged={() => { setSelected(null); refresh(); }}
                />
            )}

            {sensitiveReveal && (
                <SensitiveRevealModal
                    reveal={sensitiveReveal}
                    onClose={() => setSensitiveReveal(null)}
                />
            )}

            {showDiag && (
                <DiagnosticoModal
                    environment={environment}
                    onClose={() => setShowDiag(false)}
                    onAdotou={refresh}
                />
            )}

            {showTitulares && (
                <GestaoTitularesModal
                    environment={environment}
                    onClose={() => setShowTitulares(false)}
                    onAlterado={refresh}
                />
            )}
        </div>
    );
}

// =============================================================================
// FACE DO CARTÃO — modo BLOCOS (padrão)
// Memoizada: refresh de lista não repinta cartão inalterado.
// =============================================================================
const CardFace = memo(function CardFace({ card, onSelect }) {
    const meta = statusMeta(card.status);
    const rule = (card.rules && card.rules[0]) || null;

    return (
        <div
            className={`gb-card ${card.status !== 'active' ? 'gb-card--inactive' : ''}`}
            style={STARK_THEME_STYLE}
            onClick={() => onSelect(card)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(card); } }}
        >
            <div className="gb-card-pattern" />
            <div className="gb-card-status" style={{ background: meta.color }}>
                {card.status !== 'active' && '🔒 '}{meta.label}
            </div>
            <div className="gb-card-top">
                <div className="gb-chip" />
                <span className="gb-tipo-badge gb-tipo-badge--stark">
                    Stark Bank · {typeLabelOf(card)}
                </span>
            </div>
            {card.origem === 'stark' && (
                <span className="gb-card-origem" title="Emitido direto no Web Banking da Stark, fora desta plataforma">
                    ⧉ fora da plataforma
                </span>
            )}
            {rule && (
                <div className="gb-card-balance">
                    <small>Limite {INTERVAL_LABEL[rule.interval] || 'mensal'}</small>
                    <h2>{fmtBRL(rule.amount)}</h2>
                </div>
            )}
            <div className="gb-card-number">{maskCard(card.last4)}</div>
            <div className="gb-card-footer">
                <div>
                    <small>Titular</small>
                    <strong>{holderNameOf(card) || '—'}</strong>
                </div>
                <div>
                    <small>{card.merchant_name ? 'Travado em' : 'Tipo'}</small>
                    <strong>{card.merchant_name || typeLabelOf(card)}</strong>
                </div>
            </div>
        </div>
    );
});

// =============================================================================
// LINHA DO CARTÃO — modo LINHAS (densidade alta, boa pra frota grande)
// =============================================================================
const CardRow = memo(function CardRow({ card, onSelect }) {
    const meta = statusMeta(card.status);
    const rule = (card.rules && card.rules[0]) || null;

    return (
        <div
            className={`gb-row ${card.status !== 'active' ? 'gb-row--inactive' : ''}`}
            role="row"
            tabIndex={0}
            onClick={() => onSelect(card)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(card); } }}
        >
            <span className="gb-row-holder" role="cell">
                <span className="gb-row-avatar" aria-hidden="true">
                    {(holderNameOf(card) || '?').charAt(0).toUpperCase()}
                </span>
                <span className="gb-row-holder-text">
                    <strong>{holderNameOf(card) || '—'}</strong>
                    {card.merchant_name && <small>🔒 {card.merchant_name}</small>}
                    {card.origem === 'stark' && (
                        <small className="gb-row-origem" title="Emitido no Web Banking da Stark">⧉ fora da plataforma</small>
                    )}
                </span>
            </span>

            <span className="gb-row-num" role="cell">•••• {card.last4 || '????'}</span>

            <span className="gb-row-type" role="cell">
                <span className={`gb-row-typebadge ${card.type === 'physical' ? 'is-physical' : 'is-virtual'}`}>
                    {typeLabelOf(card)}
                </span>
            </span>

            <span className="gb-row-limit" role="cell">
                {rule ? (
                    <>
                        <strong>{fmtBRL(rule.amount)}</strong>
                        <small>{INTERVAL_LABEL[rule.interval] || 'mensal'}</small>
                    </>
                ) : (
                    <small>sem regra</small>
                )}
            </span>

            <span className="gb-row-status" role="cell">
                <span className="gb-status-pill" style={{ background: `${meta.color}1f`, color: meta.color }}>
                    {meta.label}
                </span>
            </span>
        </div>
    );
});

// =============================================================================
// MODAL — EMITIR CARTÃO
// =============================================================================
function IssueCardModal({ holders, scope, environment, onClose, onCreated }) {
    const [holderMode, setHolderMode] = useState(holders.length ? 'existing' : 'new');
    const [holderId, setHolderId] = useState(holders[0]?.stark_holder_id || '');
    const [newHolderName, setNewHolderName] = useState('');
    const [newHolderTaxId, setNewHolderTaxId] = useState('');

    const [type, setType] = useState('virtual');
    const [defaultAddr, setDefaultAddr] = useState(() => ADDR_CACHE.data);
    const [limite, setLimite] = useState('1000');
    const [interval, setInterval_] = useState('month');
    const [categorias, setCategorias] = useState([]);
    const [lockMerchant, setLockMerchant] = useState(false);
    const [merchantName, setMerchantName] = useState('');
    const [creating, setCreating] = useState(false);

    useEscapeKey(() => { if (!creating) onClose(); });

    // Endereço de envio só é buscado quando o cartão vira físico (e só 1x).
    useEffect(() => {
        if (type !== 'physical' || defaultAddr) return undefined;
        let alive = true;
        const controller = new AbortController();
        loadWorkspaceAddress({ signal: controller.signal })
            .then((d) => { if (alive) setDefaultAddr(d); })
            .catch(() => { if (alive) setDefaultAddr({ available: false }); });
        return () => { alive = false; controller.abort(); };
    }, [type, defaultAddr]);

    const selectedHolderName = holderMode === 'existing'
        ? holders.find((h) => h.stark_holder_id === holderId)?.name
        : newHolderName.trim();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (creating) return; // guarda de duplo submit

        // Emitir cartão é irreversível e custa dinheiro real. Se o ambiente
        // mudou desde que este modal abriu, aborta antes de qualquer chamada.
        if (!escopoAindaVale(scope)) { avisarEscopoMudou(); return; }

        if (holderMode === 'existing' && !holderId) { toast.error('Selecione um titular.'); return; }
        if (holderMode === 'new' && !newHolderName.trim()) { toast.error('Informe o nome do novo titular.'); return; }
        if (holderMode === 'new' && !taxIdValido(newHolderTaxId)) { toast.error('CPF/CNPJ do titular inválido.'); return; }

        const valor = parseLimite(limite);
        if (valor === null) { toast.error(`Informe um limite entre R$ 0 e R$ ${LIMITE_MAX.toLocaleString('pt-BR')}.`); return; }
        if (lockMerchant && !merchantName.trim()) { toast.error('Informe o estabelecimento ou desative a trava.'); return; }

        setCreating(true);
        try {
            const payload = {
                type,
                rules: valor > 0 ? [{
                    name: 'Limite Principal',
                    amount: valor,
                    interval,
                    ...(categorias.length ? { categories: categoriasToPayload(categorias) } : {}),
                }] : [],
                display_name: selectedHolderName,
                ...(lockMerchant && merchantName.trim() ? { merchant_name: merchantName.trim() } : {}),
            };
            if (holderMode === 'existing') payload.holder_id = holderId;
            else payload.new_holder = { name: newHolderName.trim(), tax_id: newHolderTaxId.replace(/\D/g, '') };

            const { data } = await api.post('/api/baas/account/cards', payload);
            invalidateCardsCache();
            toast.success('Cartão emitido com sucesso!');
            onCreated(data);
        } catch (e2) {
            const msg = apiErr(e2, 'Não foi possível emitir o cartão.');
            if (msg) toast.error(msg);
            setCreating(false);
        }
    };

    return (
        <div className="gb-modal-overlay" onClick={() => { if (!creating) onClose(); }}>
            <div
                className="gb-modal gb-modal--wide"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Emitir novo cartão"
            >
                <div className="gb-modal-header gb-modal-header--dark">
                    <h3>
                        + Emitir novo cartão
                        {environment && (
                            <span className={`gb-env-badge ${environment === 'production' ? 'is-prod' : 'is-sbx'}`}>
                                ● {envLabelOf(environment)}
                            </span>
                        )}
                    </h3>
                    <button className="gb-modal-close" onClick={onClose} disabled={creating} aria-label="Fechar">✕</button>
                </div>

                <form className="gb-modal-form" onSubmit={handleSubmit}>
                    <div className="gb-modal-body gb-modal-body--2col">
                        {/* ── Coluna esquerda: titular e tipo ───────────────── */}
                        <section className="gb-col">
                            <div className="gb-section-title">Titular (Holder)</div>
                            <div className="gb-tipo-select">
                                <button
                                    type="button"
                                    className={`gb-tipo-option ${holderMode === 'existing' ? 'active' : ''}`}
                                    onClick={() => setHolderMode('existing')}
                                    disabled={!holders.length}
                                >
                                    <strong>Titular existente</strong>
                                    <small>{holders.length ? `${holders.length} cadastrado(s)` : 'Nenhum ainda'}</small>
                                </button>
                                <button
                                    type="button"
                                    className={`gb-tipo-option ${holderMode === 'new' ? 'active' : ''}`}
                                    onClick={() => setHolderMode('new')}
                                >
                                    <strong>Criar novo titular</strong>
                                    <small>Ex: motorista, veículo, setor</small>
                                </button>
                            </div>

                            {holderMode === 'existing' ? (
                                <div className="gb-form-field">
                                    <label htmlFor="gb-holder-sel">Selecionar titular</label>
                                    <select id="gb-holder-sel" value={holderId} onChange={(e) => setHolderId(e.target.value)}>
                                        {holders.map((h) => (
                                            <option key={h.stark_holder_id} value={h.stark_holder_id}>{h.name}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="gb-form-row">
                                    <div className="gb-form-field">
                                        <label htmlFor="gb-holder-name">Nome do titular *</label>
                                        <input
                                            id="gb-holder-name"
                                            value={newHolderName}
                                            onChange={(e) => setNewHolderName(e.target.value)}
                                            placeholder="Ex: Motorista Silva"
                                            maxLength={120}
                                            required
                                        />
                                    </div>
                                    <div className="gb-form-field">
                                        <label htmlFor="gb-holder-tax">CPF/CNPJ (opcional)</label>
                                        <input
                                            id="gb-holder-tax"
                                            value={newHolderTaxId}
                                            onChange={(e) => setNewHolderTaxId(e.target.value)}
                                            inputMode="numeric"
                                            maxLength={18}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="gb-section-title">Tipo do cartão</div>
                            <div className="gb-tipo-select gb-tipo-select--sm">
                                <button type="button" className={`gb-tipo-option ${type === 'virtual' ? 'active' : ''}`} onClick={() => setType('virtual')}>
                                    <strong>Virtual</strong>
                                    <small>Ativo na hora</small>
                                </button>
                                <button type="button" className={`gb-tipo-option ${type === 'physical' ? 'active' : ''}`} onClick={() => setType('physical')}>
                                    <strong>Físico</strong>
                                    <small>Correio · nasce pendente</small>
                                </button>
                            </div>

                            {type === 'physical' && (
                                <div className="gb-addr-note">
                                    {!defaultAddr ? (
                                        'Carregando endereço de envio...'
                                    ) : defaultAddr.available ? (
                                        <>
                                            Envio ao <strong>endereço padrão do Workspace</strong>:
                                            <br />{defaultAddr.streetLine1}{defaultAddr.streetLine2 ? `, ${defaultAddr.streetLine2}` : ''}
                                            <br />{[defaultAddr.district, defaultAddr.city, defaultAddr.stateCode].filter(Boolean).join(' · ')}{defaultAddr.zipCode ? ` · CEP ${defaultAddr.zipCode}` : ''}
                                            <br /><br />Para outro endereço, altere no <strong>Web Banking da Stark Bank</strong> — a API de emissão não permite endereço por cartão.
                                        </>
                                    ) : (
                                        <>Envio ao <strong>endereço cadastrado no Workspace da Stark Bank</strong>. Para trocar, use o Web Banking — a API de emissão não aceita endereço por cartão.</>
                                    )}
                                </div>
                            )}
                        </section>

                        {/* ── Coluna direita: limite, categorias, estabelecimento ── */}
                        <section className="gb-col">
                            <div className="gb-section-title">Limite de gasto</div>
                            <div className="gb-form-row">
                                <div className="gb-form-field">
                                    <label htmlFor="gb-issue-limite">Valor (R$)</label>
                                    <input
                                        id="gb-issue-limite"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max={LIMITE_MAX}
                                        value={limite}
                                        onChange={(e) => setLimite(e.target.value)}
                                    />
                                </div>
                                <div className="gb-form-field">
                                    <label htmlFor="gb-issue-interval">Intervalo</label>
                                    <select id="gb-issue-interval" value={interval} onChange={(e) => setInterval_(e.target.value)}>
                                        <option value="day">Diário</option>
                                        <option value="week">Semanal</option>
                                        <option value="month">Mensal</option>
                                    </select>
                                </div>
                            </div>

                            <div className="gb-section-title">Categorias</div>
                            <CategoryCombobox selected={categorias} onChange={setCategorias} />

                            <div className="gb-section-title">Estabelecimento</div>
                            <label className="gb-create-toggle-item">
                                <span>🔒 Travar em um único estabelecimento</span>
                                <input type="checkbox" checked={lockMerchant} onChange={(e) => setLockMerchant(e.target.checked)} />
                            </label>
                            {lockMerchant && (
                                <div className="gb-form-field">
                                    <label htmlFor="gb-merchant">Nome do estabelecimento</label>
                                    <input
                                        id="gb-merchant"
                                        value={merchantName}
                                        onChange={(e) => setMerchantName(e.target.value)}
                                        placeholder="Ex: posto shell, uber"
                                        maxLength={60}
                                    />
                                </div>
                            )}
                        </section>
                    </div>

                    <div className="gb-modal-footer">
                        <button type="button" className="gb-btn-cancel" onClick={onClose} disabled={creating}>Cancelar</button>
                        <button type="submit" className="gb-btn-submit" disabled={creating}>
                            {creating ? 'Emitindo...' : 'Emitir cartão'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// =============================================================================
// MODAL — DETALHE / GESTÃO DO CARTÃO
// =============================================================================
/**
 * ⚠️ O QUE ESTA TELA APRENDEU A NÃO FAZER: ler `card.rules[0]` e concluir.
 *
 * Na Stark o TITULAR carrega o controle de gastos e o cartão HERDA. Cartão
 * emitido no Web Banking normalmente não tem regra própria — e a versão anterior
 * mostrava campos vazios, como se aquele cartão não tivesse limite nenhum. Tinha:
 * um nível acima. Exibir "sem limite" para um cartão limitado é pior que não
 * exibir nada, porque leva alguém a configurar um limite que já existia.
 *
 * Agora a tela consome `GET /cards/:id/detalhe`, que devolve as duas origens
 * separadas mais a leitura do que vale de fato, e mostra:
 *
 *   • LIMITE EFETIVO, com selo dizendo se veio do cartão ou do titular
 *   • quando não há regra em nenhum nível, o aviso de que o cartão acessa o
 *     saldo inteiro da empresa (é o mesmo alerta que o Web Banking dá)
 *   • ONDE pode passar: categorias, países, métodos de captura
 *   • PARA QUÊ pode ser usado: compra e/ou saque em ATM
 *   • QUANDO pode passar: janela de horário/dias
 *   • as regras do titular em modo leitura, para não editar no lugar errado
 */
function CardDetailModal({ card, scope, environment, onClose, onChanged }) {
    const [carregando, setCarregando] = useState(true);
    const [detalhe, setDetalhe] = useState(null);
    const [erroDetalhe, setErroDetalhe] = useState(null);

    const [saving, setSaving] = useState(false);
    const [busyStatus, setBusyStatus] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [unlockPhone, setUnlockPhone] = useState('');
    const [sendingUnlock, setSendingUnlock] = useState(false);

    // Estado editável da regra DO CARTÃO (a do titular é read-only aqui).
    // Nota: a janela de horário (schedule) NÃO entra aqui de propósito — ela é
    // regra do titular (o SDK aplica as regras do holder a todos os cartões), e
    // no cartão aparece apenas em leitura (ver ScheduleHerdado).
    const [limite, setLimite] = useState('');
    const [interval, setInterval_] = useState('month');
    const [categorias, setCategorias] = useState([]);
    const [paises, setPaises] = useState([]);
    const [metodos, setMetodos] = useState([]);
    const [propositos, setPropositos] = useState([]);

    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const { catalogos } = useRuleCatalogs();

    const meta = statusMeta(detalhe?.card?.status || card.status);
    const isPhysical = (detalhe?.card?.type || card.type) === 'physical';
    const busy = saving || busyStatus || sendingUnlock || carregando;
    const cardId = card.stark_card_id;

    // ── Carga do detalhe ────────────────────────────────────────────────────
    const carregar = useCallback(async (signal) => {
        setCarregando(true);
        setErroDetalhe(null);
        try {
            const { data } = await api.get(
                `/api/baas/account/cards/${encodeURIComponent(cardId)}/detalhe`,
                { signal },
            );
            if (!aliveRef.current) return;
            setDetalhe(data);

            // A regra do CARTÃO é a base do formulário. Se ele não tem regra
            // própria, os campos ficam vazios de propósito: preencher com a do
            // titular faria o operador "salvar" uma cópia sem perceber, criando
            // uma regra de cartão que antes não existia.
            const doCartao = asArray(data?.card?.rules)[0] || null;
            setLimite(doCartao?.amount != null ? String(doCartao.amount) : '');
            setInterval_(doCartao?.interval || 'month');
            setCategorias(categoriasFromRule(doCartao?.categories));
            setPaises(asArray(doCartao?.countries).map((c) => c?.code).filter(Boolean));
            setMetodos(asArray(doCartao?.methods).map((m) => m?.code).filter(Boolean));
            setPropositos(asArray(doCartao?.purposes).filter(Boolean));
        } catch (e) {
            const msg = apiErr(e, 'Não foi possível carregar o detalhe do cartão.');
            if (msg && aliveRef.current) setErroDetalhe(msg);
        } finally {
            if (aliveRef.current) setCarregando(false);
        }
    }, [cardId]);

    useEffect(() => {
        const controller = new AbortController();
        carregar(controller.signal);
        return () => controller.abort();
    }, [carregar]);

    // ── Alterações pendentes ────────────────────────────────────────────────
    const inicial = useMemo(() => {
        const r = asArray(detalhe?.card?.rules)[0] || null;
        return {
            limite: r?.amount != null ? String(r.amount) : '',
            interval: r?.interval || 'month',
            categorias: categoriasFromRule(r?.categories),
            paises: asArray(r?.countries).map((c) => c?.code).filter(Boolean),
            metodos: asArray(r?.methods).map((m) => m?.code).filter(Boolean),
            propositos: asArray(r?.purposes).filter(Boolean),
        };
    }, [detalhe]);

    const mesmoConjunto = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
    const dirty = useMemo(() => (
        limite !== inicial.limite
        || interval !== inicial.interval
        || !mesmoConjunto(categorias, inicial.categorias)
        || !mesmoConjunto(paises, inicial.paises)
        || !mesmoConjunto(metodos, inicial.metodos)
        || !mesmoConjunto(propositos, inicial.propositos)
    ), [limite, interval, categorias, paises, metodos, propositos, inicial]);

    const discardArmedRef = useRef(false);
    const attemptClose = useCallback(() => {
        if (busy) return;
        if (dirty && !discardArmedRef.current) {
            discardArmedRef.current = true;
            setTimeout(() => { discardArmedRef.current = false; }, 6000);
            toast.info('Há alterações não salvas. Clique de novo para descartar, ou use "Salvar regra".', { toastId: 'gb-dirty' });
            return;
        }
        onClose();
    }, [busy, dirty, onClose]);

    useEscapeKey(attemptClose);

    // ── Mutações ────────────────────────────────────────────────────────────
    const saveRules = async () => {
        if (saving) return;
        if (!escopoAindaVale(scope)) { avisarEscopoMudou(); return; }

        const valor = parseLimite(limite);
        if (valor === null) { toast.error(`Informe um limite entre R$ 0 e R$ ${LIMITE_MAX.toLocaleString('pt-BR')}.`); return; }

        // Propósito vazio significa "todos" para a Stark. Marcar NENHUM dos dois
        // não bloqueia nada — e o operador sairia achando que travou o cartão.
        if (propositos.length === 0 && inicial.propositos.length > 0) {
            toast.error('Desmarcar todos os usos não bloqueia nada: a Stark libera compra e saque. Marque ao menos um.');
            return;
        }

        setSaving(true);
        try {
            // ⚠️ A janela de horário (schedule) NÃO é enviada aqui de propósito.
            // Ela é regra do TITULAR (o SDK da Stark aplica as regras do holder a
            // todos os cartões dele), e passou a ser editável apenas em 👤 Titulares.
            // Como a regra do cartão SUBSTITUI o conjunto anterior na Stark, não
            // reenviar schedule também limpa qualquer janela residual que tenha
            // ficado no nível do cartão — o que é o comportamento desejado: o
            // horário passa a morar num lugar só, o titular.
            const regra = {
                name: 'Limite Principal',
                amount: valor,
                interval,
                ...(categorias.length ? { categories: categoriasToPayload(categorias) } : {}),
                ...(paises.length ? { countries: paises.map((code) => ({ code })) } : {}),
                ...(metodos.length ? { methods: metodos.map((code) => ({ code })) } : {}),
                ...(propositos.length ? { purposes: propositos } : {}),
            };
            await api.patch(`/api/baas/account/cards/${encodeURIComponent(cardId)}/rules`, { rules: [regra] });
            invalidateCardsCache();
            toast.success('Regras do cartão atualizadas!');
            onChanged();
        } catch (e) {
            const msg = apiErr(e, 'Não foi possível atualizar as regras.');
            if (msg) toast.error(msg);
            setSaving(false);
        }
    };

    const setStatus = async (status) => {
        if (busyStatus) return;
        if (!escopoAindaVale(scope)) { avisarEscopoMudou(); return; }
        setBusyStatus(true);
        try {
            await api.patch(`/api/baas/account/cards/${encodeURIComponent(cardId)}/status`, { status });
            invalidateCardsCache();
            toast.success(
                status === 'blocked' ? 'Cartão bloqueado.'
                    : status === 'active' ? 'Cartão reativado.'
                        : 'Cartão cancelado.',
            );
            onChanged();
        } catch (e) {
            const msg = apiErr(e, 'Não foi possível alterar o status do cartão.');
            if (msg) toast.error(msg);
            setBusyStatus(false);
        }
    };

    const handleSendUnlock = async () => {
        if (sendingUnlock) return;
        if (!escopoAindaVale(scope)) { avisarEscopoMudou(); return; }
        const digits = unlockPhone.replace(/\D/g, '');
        if (digits.length < 10 || digits.length > 13) { toast.error('Informe o WhatsApp do titular com DDD.'); return; }
        setSendingUnlock(true);
        try {
            await api.post(`/api/baas/account/cards/${encodeURIComponent(cardId)}/send-unlock`, { phone: digits });
            toast.success('Link de desbloqueio enviado por WhatsApp!');
        } catch (e) {
            const msg = apiErr(e, 'Falha ao enviar o link de desbloqueio.');
            if (msg) toast.error(msg);
        } finally {
            setSendingUnlock(false);
        }
    };

    const efetiva = detalhe?.efetiva || null;
    const titular = detalhe?.titular || null;
    const cardData = detalhe?.card || card;
    const status = cardData?.status || card.status;

    return (
        <div className="gb-modal-overlay" onClick={attemptClose}>
            <div
                className="gb-modal gb-modal--wide gb-modal--xwide"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`Cartão de ${holderNameOf(cardData) || 'titular'}`}
            >
                <div className="gb-modal-header" style={STARK_THEME_STYLE}>
                    <div className="gb-modal-card-mini">
                        <div>
                            <h3>{holderNameOf(cardData) || 'Cartão'}</h3>
                            <span>{maskCard(cardData.last4)}</span>
                        </div>
                        <div className="gb-modal-balance">
                            <small>Tipo</small>
                            <strong>{isPhysical ? 'Físico' : 'Virtual'}</strong>
                        </div>
                    </div>
                    <div className="gb-modal-header-badges">
                        <span style={{ background: meta.color }}>{meta.label}</span>
                        {environment && (
                            <span className={`gb-env-badge ${environment === 'production' ? 'is-prod' : 'is-sbx'}`}>
                                ● {envLabelOf(environment)}
                            </span>
                        )}
                    </div>
                    <button className="gb-modal-close" onClick={onClose} disabled={busy} aria-label="Fechar">✕</button>
                </div>

                <div className="gb-modal-body">
                    {carregando ? (
                        <div className="gb-diag-loading">
                            <div className="gb-skel-line" />
                            <div className="gb-skel-line" />
                            <div className="gb-skel-line" />
                            <span>Lendo o cartão e as regras do titular na Stark Bank...</span>
                        </div>
                    ) : erroDetalhe ? (
                        <div className="gb-diag-empty">
                            <p>{erroDetalhe}</p>
                            <button type="button" className="gb-btn-cancel" onClick={() => carregar()}>Tentar de novo</button>
                        </div>
                    ) : (
                        <>
                            {cardData.origem === 'stark' && (
                                <div className="gb-stark-info-box">
                                    <strong>Cartão emitido fora da plataforma.</strong> Ele nasceu no Web Banking
                                    da Stark Bank, então dados que a BLB normalmente guarda (apelido, estabelecimento
                                    travado, quem emitiu) podem estar vazios. Bloquear, cancelar e alterar regras
                                    funcionam normalmente.
                                </div>
                            )}

                            {/* Layout em 3 colunas: o "controle em vigor" ganha
                                coluna própria — some o efeito de ficar sobreposto
                                ao rolar (era sticky sobre o corpo). As duas colunas
                                de edição ficam mais largas. Empilha em telas
                                estreitas via CSS (.gb-detail-3col). */}
                            <div className="gb-detail-3col">
                                {/* ── COLUNA 1: o que vale de fato ─────────── */}
                                <aside className="gb-detail-c1">
                                    {/* ── PAINEL DO CONTROLE EFETIVO ────────── */}
                                    <RegraEfetivaPainel efetiva={efetiva} titular={titular} />
                                </aside>

                                {/* ── COLUNA 2: quanto e quando ───────────── */}
                                <section className="gb-col gb-detail-c2">
                                    <div className="gb-section-title">Limite deste cartão</div>
                                    <p className="gb-field-hint">
                                        Regra própria do cartão. Vazio = o cartão segue apenas o limite do titular.
                                    </p>
                                    <div className="gb-form-row">
                                        <div className="gb-form-field">
                                            <label htmlFor="gb-detail-limite">Valor (R$)</label>
                                            <input
                                                id="gb-detail-limite"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max={LIMITE_MAX}
                                                value={limite}
                                                onChange={(e) => setLimite(e.target.value)}
                                                disabled={busy}
                                                placeholder="sem regra própria"
                                            />
                                        </div>
                                        <div className="gb-form-field">
                                            <label htmlFor="gb-detail-interval">Renova a cada</label>
                                            <select
                                                id="gb-detail-interval"
                                                value={interval}
                                                onChange={(e) => setInterval_(e.target.value)}
                                                disabled={busy}
                                            >
                                                {asArray(catalogos?.intervals).map((i) => (
                                                    <option key={i.code} value={i.code}>{i.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Explicação simples da relação cartão × titular: os dois limites
                                        coexistem e a compra precisa caber nos dois (o mais restritivo vale). */}
                                    <div className="gb-infobox" role="note">
                                        <span className="gb-infobox-icon" aria-hidden="true">ℹ</span>
                                        <div className="gb-infobox-texto">
                                            Este limite vale <strong>junto</strong> com o limite do titular — não no lugar dele.
                                            A compra precisa caber nos dois, então na prática vale o <strong>mais restritivo</strong>.
                                            Deixe em branco para o cartão seguir apenas o limite do titular.
                                        </div>
                                    </div>

                                    <div className="gb-section-title">Para que pode ser usado</div>
                                    <p className="gb-field-hint">
                                        Desmarcar <strong>Saque</strong> impede retirada em caixa eletrônico — o uso
                                        mais difícil de auditar num cartão de frota.
                                    </p>
                                    <ChipPicker
                                        opcoes={asArray(catalogos?.purposes)}
                                        selecionados={propositos}
                                        onChange={setPropositos}
                                        disabled={busy}
                                        vazioLabel="Compras e saques liberados"
                                    />

                                    <div className="gb-section-title">Quando pode passar</div>
                                    {/* A janela de horário é regra do TITULAR (o SDK da Stark aplica as
                                        regras do holder a todos os cartões dele). Aqui é só leitura:
                                        mostra o que vale, herdado do titular; a edição fica em 👤 Titulares. */}
                                    <ScheduleHerdado titular={titular} />
                                </section>

                                {/* ── COLUNA 2: onde ──────────────────────── */}
                                <section className="gb-col gb-detail-c3">
                                    <div className="gb-section-title">Onde pode passar</div>

                                    {/* ⚠️ Sem este aviso, o combobox vazio faz parecer que o cartão
                                        aceita tudo — quando o titular pode ter uma whitelist que já
                                        restringe. Foi exatamente o caso do cartão com "Posto_DM" no
                                        titular e nada no cartão. */}
                                    <RestricaoHerdada titular={titular} />

                                    <CategoryCombobox selected={categorias} onChange={setCategorias} disabled={busy} />

                                    <div className="gb-section-title">Forma de pagamento</div>
                                    <p className="gb-field-hint">
                                        Vazio = <strong>todas</strong> as formas aceitas. Ao marcar, o cartão passa a
                                        aceitar <strong>somente</strong> as marcadas. Toque em cada uma para ver o que
                                        significa; <strong>Tarja</strong> e <strong>Digitado</strong> são as mais
                                        arriscadas de manter num cartão de frota.
                                    </p>
                                    <ChipPicker
                                        opcoes={asArray(catalogos?.methods)}
                                        selecionados={metodos}
                                        onChange={setMetodos}
                                        disabled={busy}
                                        descricoes={METHOD_HELP}
                                        vazioLabel="Todas as formas aceitas"
                                    />
                                    {/* Legenda das formas MARCADAS — o tooltip só
                                        aparece no hover (some no celular); esta
                                        lista deixa o significado sempre à vista.
                                        Puro texto, não altera o que é enviado. */}
                                    {metodos.length > 0 && (
                                        <div className="gb-field-hint" style={{ marginTop: 6 }}>
                                            {metodos.map((code) => (METHOD_HELP[code] ? (
                                                <div key={code} style={{ marginTop: 3 }}>
                                                    <strong>{METHOD_LABEL[code] || code}:</strong> {METHOD_HELP[code]}
                                                </div>
                                            ) : null))}
                                        </div>
                                    )}

                                    <div className="gb-section-title">Países</div>
                                    <PaisPicker
                                        opcoes={asArray(catalogos?.countries)}
                                        selecionados={paises}
                                        onChange={setPaises}
                                        disabled={busy}
                                    />
                                </section>
                            </div>

                            {/* ── AÇÕES DO CARTÃO ─────────────────────────── */}
                            <div className="gb-section-title">Ações do cartão</div>
                            <div className="gb-actions-row">
                                {status === 'active' && (
                                    <button className="gb-action-btn gb-action-orange" onClick={() => setStatus('blocked')} disabled={busy}>
                                        🔒 Bloquear temporariamente
                                    </button>
                                )}
                                {status === 'blocked' && (
                                    <button className="gb-action-btn gb-action-green" onClick={() => setStatus('active')} disabled={busy}>
                                        🔓 Reativar
                                    </button>
                                )}
                                {status !== 'canceled' && (
                                    <button className="gb-action-btn gb-action-red" onClick={() => setShowCancelConfirm(true)} disabled={busy}>
                                        ✕ Cancelar definitivamente
                                    </button>
                                )}
                            </div>
                            <p className="gb-field-hint">
                                <strong>Bloquear</strong> é reversível e recusa compras na hora. <strong>Cancelar</strong> é
                                permanente: o cartão não volta, e emitir outro gera nova via.
                            </p>

                            {showCancelConfirm && (
                                <div className="gb-cancel-warning">
                                    <strong>Esta ação é permanente.</strong>
                                    <p>Digite <strong>CANCELAR</strong> para confirmar o cancelamento deste cartão.</p>
                                    <div className="gb-form-field">
                                        <input
                                            value={confirmText}
                                            onChange={(e) => setConfirmText(e.target.value)}
                                            placeholder="CANCELAR"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="gb-form-actions">
                                        <button className="gb-btn-cancel" onClick={() => { setShowCancelConfirm(false); setConfirmText(''); }} disabled={busy}>
                                            Voltar
                                        </button>
                                        <button
                                            className="gb-btn-red"
                                            disabled={confirmText.trim().toUpperCase() !== 'CANCELAR' || busy}
                                            onClick={() => setStatus('canceled')}
                                        >
                                            {busyStatus ? 'Cancelando...' : 'Confirmar cancelamento'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {status === 'pending' && (
                                <div className="gb-stark-info-box">
                                    <strong>Desbloquear cartão físico.</strong> O titular conclui na página segura da Stark:
                                    informa os 4 últimos dígitos (<strong>{cardData.last4 || '????'}</strong>) + o celular dele,
                                    recebe um código por WhatsApp e cadastra o PIN. A central só entrega o link.
                                    <div className="gb-form-row" style={{ marginTop: 10 }}>
                                        <div className="gb-form-field">
                                            <label htmlFor="gb-unlock-phone">WhatsApp do titular (com DDD)</label>
                                            <input
                                                id="gb-unlock-phone"
                                                type="tel"
                                                inputMode="numeric"
                                                placeholder="(12) 99999-9999"
                                                value={unlockPhone}
                                                onChange={(e) => setUnlockPhone(e.target.value)}
                                                maxLength={20}
                                                disabled={busy}
                                            />
                                        </div>
                                        <div className="gb-form-field gb-form-field--bottom">
                                            <button type="button" className="gb-btn-blue" onClick={handleSendUnlock} disabled={busy}>
                                                {sendingUnlock ? 'Enviando...' : '📲 Enviar'}
                                            </button>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="gb-btn-cancel gb-btn-cancel--full"
                                        onClick={() => {
                                            if (!navigator.clipboard) { toast.error('Navegador sem acesso à área de transferência.'); return; }
                                            navigator.clipboard.writeText(STARK_UNLOCK_URL)
                                                .then(() => toast.info('Link copiado.'))
                                                .catch(() => toast.error('Não foi possível copiar o link.'));
                                        }}
                                    >
                                        Copiar link de desbloqueio
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="gb-modal-footer">
                    {dirty && <span className="gb-dirty-flag">Alterações não salvas</span>}
                    <button type="button" className="gb-btn-cancel" onClick={attemptClose} disabled={busy}>Fechar</button>
                    <button type="button" className="gb-btn-submit" onClick={saveRules} disabled={saving || busyStatus || carregando || !dirty}>
                        {saving ? 'Salvando...' : '💾 Salvar regra do cartão'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// AVISO DE RESTRIÇÃO HERDADA DO TITULAR
// =============================================================================
/**
 * O campo de categorias do modal edita a regra DO CARTÃO. Quando ele está vazio
 * e o TITULAR tem whitelist, a leitura natural — "este cartão aceita qualquer
 * categoria" — está errada: a restrição do titular também vale.
 *
 * Este aviso existe para que ninguém configure uma whitelist redundante no
 * cartão, ou pior: conclua que não há restrição nenhuma e libere um gasto que a
 * Stark vai recusar no caixa.
 */
function RestricaoHerdada({ titular }) {
    const { items: catalogo } = useCategoryCatalog(true);

    const herdadas = useMemo(() => {
        const mapa = catalogo?.length ? catalogMap(catalogo) : null;
        const out = [];
        for (const r of asArray(titular?.rules)) {
            for (const c of asArray(r.categories)) {
                const rotulo = rotuloCategoria(c, mapa);
                if (rotulo && !out.includes(rotulo)) out.push(rotulo);
            }
        }
        return out;
    }, [titular, catalogo]);

    if (herdadas.length === 0) return null;

    return (
        <div className="gb-herdado">
            <strong>O titular já restringe onde este cartão pode passar.</strong>
            <div className="gb-herdado-itens">
                {herdadas.map((h) => <span key={h} className="gb-herdado-item">{h}</span>)}
            </div>
            <p>
                Esta lista vem de <strong>{titular?.name || 'titular'}</strong> e vale para todos os cartões dele.
                Marcar categorias abaixo <u>estreita ainda mais</u> — a compra precisa passar nas duas listas.
                Para alterar a herança, use <strong>👤 Titulares</strong>.
            </p>
        </div>
    );
}

// =============================================================================
// JANELA DE HORÁRIO HERDADA DO TITULAR (somente leitura no cartão)
// =============================================================================
/**
 * A janela de horário (schedule) é uma regra de gasto do TITULAR — o SDK da
 * Stark é explícito: as regras do holder "apply to all underlying cards". Por
 * isso, no modal do cartão, o horário é apresentado apenas em LEITURA: mostra o
 * que vale (herdado do titular), mas a edição acontece na tela de Titulares.
 *
 * Reaproveita o mesmo ScheduleBuilder, desabilitado (cinza), para o operador ver
 * exatamente os mesmos dias/horário que veria ao editar o titular — sem poder
 * alterar aqui. Se o titular não tem janela, deixa isso explícito em vez de
 * simplesmente sumir (ausência de janela = qualquer horário liberado).
 */
function ScheduleHerdado({ titular }) {
    // Primeira regra do titular que tenha schedule (é onde a janela mora).
    const scheduleTitular = useMemo(() => {
        for (const r of asArray(titular?.rules)) {
            if (r?.schedule && String(r.schedule).trim()) return String(r.schedule);
        }
        return '';
    }, [titular]);

    return (
        <div className="gb-herdado">
            <strong>Definido no titular — vale para todos os cartões dele.</strong>
            {scheduleTitular ? (
                <>
                    <div className="gb-herdado-schedule" aria-readonly="true">
                        {/* ScheduleBuilder desabilitado: reflete a janela do titular,
                            sem permitir edição. onChange é no-op de propósito. */}
                        <ScheduleBuilder value={scheduleTitular} onChange={() => {}} disabled />
                    </div>
                    <p>
                        Esta janela vem de <strong>{titular?.name || 'titular'}</strong>. Para alterar os dias e
                        horários, use <strong>👤 Titulares</strong> — a mudança passa a valer para todos os
                        cartões desse titular.
                    </p>
                </>
            ) : (
                <p>
                    <strong>{titular?.name || 'Este titular'}</strong> não tem janela de horário definida — os
                    cartões dele podem passar em <strong>qualquer dia e horário</strong>. Para restringir, use
                    <strong> 👤 Titulares</strong>.
                </p>
            )}
        </div>
    );
}

// =============================================================================
// PAINEL DA REGRA EFETIVA — o que vale de fato para este cartão
// =============================================================================
/**
 * Responde a pergunta que o operador realmente tem: "quanto este cartão pode
 * gastar, e por quê?".
 *
 * O caso `sem_controle` merece o destaque que tem: sem regra no cartão E sem
 * regra no titular, a Stark libera o saldo inteiro da empresa. É o mesmo aviso
 * que o Web Banking dá, e esconder isso numa tela de gestão de frota seria
 * negligência.
 */
function RegraEfetivaPainel({ efetiva, titular }) {
    if (!efetiva) return null;

    if (efetiva.sem_controle) {
        return (
            <div className="gb-efetiva gb-efetiva--alerta">
                <div className="gb-efetiva-head">
                    <span className="gb-efetiva-icone" aria-hidden="true">⚠️</span>
                    <strong>Sem controle de gastos</strong>
                </div>
                <p>
                    Não há regra neste cartão nem no titular
                    {titular?.name ? <> (<strong>{titular.name}</strong>)</> : null}. Sem regra, a Stark
                    autoriza compras até o <strong>saldo total da empresa</strong>.
                    Defina um limite abaixo, ou configure o controle no titular para valer em todos os cartões dele.
                </p>
            </div>
        );
    }

    const origemProp = efetiva.origem_limite === 'cartao';
    const intervalo = INTERVAL_LABEL_FULL[efetiva.intervalo_efetivo] || efetiva.intervalo_efetivo || '—';

    return (
        <div className="gb-efetiva">
            <div className="gb-efetiva-head">
                <span className="gb-efetiva-icone" aria-hidden="true">🛡️</span>
                <strong>Controle em vigor</strong>
                <span className={`gb-efetiva-origem ${origemProp ? 'is-card' : 'is-holder'}`}>
                    {origemProp ? 'regra do cartão' : 'herdado do titular'}
                </span>
            </div>

            <div className="gb-efetiva-valor">
                <span>{efetiva.limite_efetivo != null ? fmtBRL(efetiva.limite_efetivo) : 'sem teto de valor'}</span>
                <small>{intervalo}</small>
            </div>

            {!origemProp && titular?.name && (
                <p className="gb-efetiva-nota">
                    Este limite vem do titular <strong>{titular.name}</strong> e vale para todos os cartões dele.
                    Um limite definido aqui se aplica só a este cartão — e, se for menor, é ele que passa a valer.
                </p>
            )}

            {asArray(efetiva.regras).length > 1 && (
                <div className="gb-efetiva-lista">
                    {asArray(efetiva.regras).map((r, i) => (
                        <div key={r.id || `${r.origem}-${i}`} className="gb-efetiva-item">
                            <span className={`gb-efetiva-tag ${r.origem === 'cartao' ? 'is-card' : 'is-holder'}`}>
                                {r.origem === 'cartao' ? 'cartão' : 'titular'}
                            </span>
                            <span className="gb-efetiva-item-nome">{r.name || 'Regra'}</span>
                            <span className="gb-efetiva-item-valor">
                                {fmtBRL(r.amount)} · {INTERVAL_LABEL_FULL[r.interval] || r.interval}
                            </span>
                        </div>
                    ))}
                    <p className="gb-efetiva-nota">
                        Com mais de uma regra, quem manda é a <strong>mais restritiva</strong>.
                    </p>
                </div>
            )}

            <RestricoesResumo regras={efetiva.regras} />
        </div>
    );
}

/**
 * Resumo legível das restrições ativas, somando cartão e titular.
 *
 * ⚠️ BUG CORRIGIDO AQUI — não volte a filtrar por `name`.
 *
 * A versão anterior fazia `c?.name && cats.add(c.name)`. A Stark devolve
 * categoria de tipo abrangente SEM nome:
 *
 *     "categories": [{ "code": "", "type": "fuel" }]
 *
 * Resultado: a restrição de combustível configurada no Web Banking era
 * descartada, e a tela afirmava "nenhuma restrição de onde ou como usar" para um
 * cartão que só passava em posto. Afirmar liberdade onde existe restrição é o
 * erro mais caro que esta tela pode cometer.
 *
 * Agora o rótulo vem de `rotuloCategoria`, que cai para catálogo, dicionário de
 * tipo e, em último caso, o identificador cru.
 */
function RestricoesResumo({ regras }) {
    const itens = useMemo(() => asArray(regras), [regras]);

    // O catálogo só é buscado se houver categoria a rotular — a maioria dos
    // cartões não tem, e não faz sentido pagar a chamada por nada.
    const temCategorias = useMemo(
        () => itens.some((r) => asArray(r.categories).length > 0),
        [itens],
    );
    const { items: catalogo } = useCategoryCatalog(temCategorias);

    const resumo = useMemo(() => {
        const mapa = catalogo?.length ? catalogMap(catalogo) : null;

        // Guarda a ORIGEM de cada restrição: saber que a whitelist vem do
        // titular muda o lugar onde se vai editar.
        const cats = new Map();
        const paises = new Map();
        const metodos = new Map();
        const fins = new Map();
        const horarios = [];

        const registrar = (mapaDestino, chave, rotulo, origem) => {
            if (!chave) return;
            const atual = mapaDestino.get(chave);
            if (atual) {
                if (!atual.origens.includes(origem)) atual.origens.push(origem);
            } else {
                mapaDestino.set(chave, { rotulo, origens: [origem] });
            }
        };

        for (const r of itens) {
            const origem = r.origem || 'cartao';

            for (const c of asArray(r.categories)) {
                const chave = c?.code || c?.type || c?.name || (c?.number ? String(c.number) : null);
                registrar(cats, chave, rotuloCategoria(c, mapa) || chave, origem);
            }
            for (const c of asArray(r.countries)) {
                const chave = c?.code || c?.name;
                registrar(paises, chave, c?.name || c?.code, origem);
            }
            for (const m of asArray(r.methods)) {
                const chave = m?.code || m?.name;
                registrar(metodos, chave, METHOD_LABEL[m?.code] || m?.name || m?.code, origem);
            }
            for (const p of asArray(r.purposes)) {
                const chave = typeof p === 'string' ? p : p?.code;
                registrar(fins, chave, PURPOSE_LABEL[chave] || chave, origem);
            }
            // `schedule` pode vir como string VAZIA da Stark — falsy, e é o que
            // queremos: janela vazia não é janela.
            if (r.schedule) horarios.push({ texto: r.schedule, origem });
        }

        return {
            cats: [...cats.values()],
            paises: [...paises.values()],
            metodos: [...metodos.values()],
            fins: [...fins.values()],
            horarios,
        };
    }, [itens, catalogo]);

    const nada = !resumo.cats.length && !resumo.paises.length && !resumo.metodos.length
        && !resumo.fins.length && !resumo.horarios.length;

    if (nada) {
        return (
            <p className="gb-efetiva-nota">
                Nenhuma restrição de onde ou como usar: aceito em qualquer estabelecimento, país e forma de pagamento,
                inclusive saque.
            </p>
        );
    }

    const Origem = ({ origens }) => (
        <span className="gb-restricao-origens">
            {origens.map((o) => (
                <span key={o} className={`gb-efetiva-tag ${o === 'cartao' ? 'is-card' : 'is-holder'}`}>
                    {o === 'cartao' ? 'cartão' : 'titular'}
                </span>
            ))}
        </span>
    );

    const Lista = ({ dados, limite = 5 }) => (
        <span className="gb-restricao-itens">
            {dados.slice(0, limite).map((d, i) => (
                <span key={d.rotulo || i} className="gb-restricao-item">
                    {d.rotulo}
                    <Origem origens={d.origens} />
                </span>
            ))}
            {dados.length > limite && <span className="gb-restricao-mais">+{dados.length - limite}</span>}
        </span>
    );

    return (
        <div className="gb-restricoes">
            {resumo.cats.length > 0 && (
                <div className="gb-restricao">
                    <small>Só pode passar em ({resumo.cats.length})</small>
                    <Lista dados={resumo.cats} />
                </div>
            )}
            {resumo.fins.length > 0 && (
                <div className="gb-restricao">
                    <small>Usos permitidos</small>
                    <Lista dados={resumo.fins} />
                    {!resumo.fins.some((f) => f.rotulo === PURPOSE_LABEL.withdrawal) && (
                        <b>saque bloqueado</b>
                    )}
                </div>
            )}
            {resumo.metodos.length > 0 && (
                <div className="gb-restricao">
                    <small>Formas de pagamento</small>
                    <Lista dados={resumo.metodos} />
                </div>
            )}
            {resumo.paises.length > 0 && (
                <div className="gb-restricao">
                    <small>Países</small>
                    <Lista dados={resumo.paises} />
                </div>
            )}
            {resumo.horarios.length > 0 && (
                <div className="gb-restricao">
                    <small>Janela de uso</small>
                    <span className="gb-restricao-itens">
                        <span className="gb-restricao-item">
                            {resumo.horarios[0].texto}
                            <Origem origens={[resumo.horarios[0].origem]} />
                        </span>
                    </span>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// MODAL — REVELAÇÃO ÚNICA DOS DADOS SENSÍVEIS
// =============================================================================
function SensitiveRevealModal({ reveal, onClose }) {
    const [ack, setAck] = useState(false);

    const copy = (text, label) => {
        if (!navigator.clipboard) { toast.error('Navegador sem acesso à área de transferência.'); return; }
        navigator.clipboard.writeText(text || '')
            .then(() => toast.info(`${label} copiado.`))
            .catch(() => toast.error(`Não foi possível copiar o ${label.toLowerCase()}.`));
    };

    const validade = useMemo(() => {
        if (!reveal.expiration) return '——/——';
        const d = new Date(reveal.expiration);
        return Number.isNaN(d.getTime())
            ? '——/——'
            : d.toLocaleDateString('pt-BR', { month: '2-digit', year: '2-digit' });
    }, [reveal.expiration]);

    return (
        <div className="gb-sensitive-overlay">
            <div className="gb-sensitive-modal" role="dialog" aria-modal="true" aria-label="Dados do cartão">
                <div className="gb-sensitive-header">
                    <span className="gb-sensitive-lock" aria-hidden="true">🔐</span>
                    <h3>Dados do cartão — exibição única</h3>
                    <p>
                        Cartão do titular <strong>{reveal.holderName}</strong> emitido com sucesso.
                        Estes dados <u>não ficam armazenados</u> em nenhum lugar do sistema e
                        <u> não poderão ser recuperados</u> depois que esta janela for fechada.
                    </p>
                </div>

                <div className="gb-sensitive-fields">
                    <div className="gb-sensitive-field">
                        <label>Número do cartão</label>
                        <div className="gb-sensitive-value-row">
                            <code>{reveal.number || maskCard(reveal.last4)}</code>
                            {reveal.number && <button type="button" onClick={() => copy(reveal.number, 'Número')}>Copiar</button>}
                        </div>
                    </div>
                    <div className="gb-sensitive-row-2">
                        <div className="gb-sensitive-field">
                            <label>CVV</label>
                            <div className="gb-sensitive-value-row">
                                <code>{reveal.securityCode || '———'}</code>
                                {reveal.securityCode && <button type="button" onClick={() => copy(reveal.securityCode, 'CVV')}>Copiar</button>}
                            </div>
                        </div>
                        <div className="gb-sensitive-field">
                            <label>Validade</label>
                            <div className="gb-sensitive-value-row">
                                <code>{validade}</code>
                            </div>
                        </div>
                    </div>
                </div>

                <label className="gb-sensitive-ack">
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                    <span>Eu salvei essas informações em um local seguro e entendo que elas não serão exibidas novamente.</span>
                </label>

                <button type="button" className="gb-sensitive-close-btn" disabled={!ack} onClick={onClose}>
                    Fechar — não mostrar novamente
                </button>
            </div>
        </div>
    );
}


// =============================================================================
// PAINEL DE EGRESS — por onde as chamadas à Stark realmente saem
// =============================================================================
/**
 * Existe por causa do erro mais difícil de diagnosticar às cegas: `invalidIp`.
 * A chamada até sai — mas por um IP fora da allowlist do Project — e a tela só
 * mostra "0 cartões" sem explicar. Aqui o IP de saída REAL aparece ao lado do
 * IP do proxy configurado, e o veredito é um só: ✅ confere, ⚠️ diverge,
 * 🔴 bloqueado, ⚪ não configurado ou 🟡 não confirmado.
 *
 * 🛡️ Blindagem total (esta tela é a mais crítica do sistema): `egress` pode vir
 * ausente (backend anterior a esta correção), com campos faltando, com
 * `ip_saida` null ou com `erro`. Nenhum desses casos pode quebrar o modal — o
 * pior cenário aceitável é o painel simplesmente não aparecer. Todo acesso a
 * campo é defensivo e todo valor exibido passa por coerção de tipo.
 */
function EgressPainel({ egress }) {
    // Backend antigo (sem o campo) ou payload corrompido: some em silêncio.
    // Um bloco vazio ou um erro de render seriam piores que a ausência.
    if (!egress || typeof egress !== 'object') return null;

    const configurado = egress.configurado === true;
    const ativo = egress.ativo === true;
    const obrigatorio = egress.obrigatorio === true;
    const hostPorta = typeof egress.host_porta === 'string' ? egress.host_porta : null;
    const ipProxy = hostPorta ? hostPorta.split(':')[0] : null;

    // ip_saida é best-effort no backend: pode ser null, ter {erro}, ou {ip}.
    const ipSaidaObj = egress.ip_saida && typeof egress.ip_saida === 'object' ? egress.ip_saida : null;
    const ipReal = ipSaidaObj && typeof ipSaidaObj.ip === 'string' ? ipSaidaObj.ip : null;
    const ipErro = ipSaidaObj && typeof ipSaidaObj.erro === 'string' ? ipSaidaObj.erro : null;
    const confere = ipSaidaObj ? ipSaidaObj.confere_com_configurado : null; // true | false | null
    const erroGeral = typeof egress.erro === 'string' ? egress.erro : null;

    // ── Veredito: um estado, uma cor, uma frase acionável ────────────────────
    let nivel;      // 'ok' | 'alerta' | 'critico' | 'neutro'
    let icone;
    let titulo;
    let detalhe;

    if (obrigatorio && !ativo) {
        nivel = 'critico'; icone = '🔴';
        titulo = 'Saída bloqueada (fail-closed)';
        detalhe = 'O IP fixo de saída é obrigatório mas não está ativo, então nenhuma chamada à Stark sai — '
            + 'é o que precisa ser resolvido primeiro.' + (erroGeral ? ` Detalhe: ${erroGeral}` : '');
    } else if (confere === false) {
        nivel = 'critico'; icone = '⚠️';
        titulo = 'Saindo pelo IP errado';
        detalhe = `As chamadas estão saindo por ${ipReal || 'um IP desconhecido'}, diferente do proxy configurado`
            + `${ipProxy ? ` (${ipProxy})` : ''}. É a causa provável de invalidIp. Confira se o proxy está de pé e se `
            + 'HTTPS_PROXY/HTTP_PROXY não estão setados no Render — eles atropelam o proxy explícito.';
    } else if (confere === true) {
        nivel = 'ok'; icone = '✅';
        titulo = 'Saindo pelo IP correto';
        detalhe = `As chamadas à Stark saem por ${ipReal}, que confere com o proxy configurado. `
            + 'Esse é o IP que precisa estar na allowlist do Project — se ainda houver invalidIp, o problema é a allowlist na Stark.';
    } else if (!configurado) {
        nivel = 'alerta'; icone = '⚪';
        titulo = 'Sem IP fixo de saída';
        detalhe = ipReal
            ? `Nenhum proxy configurado — a saída depende do IP do Render (${ipReal}), que muda sem aviso e derruba a allowlist.`
            : 'Nenhum proxy de saída configurado. A saída depende do IP do Render, que muda sem aviso. '
                + 'Configure STARK_EGRESS_PROXY_URL para fixar o IP.';
    } else {
        // configurado & ativo, mas o IP de saída não pôde ser confirmado (echo falhou).
        nivel = 'neutro'; icone = '🟡';
        titulo = 'Proxy ativo — IP de saída não confirmado';
        detalhe = ipErro
            ? `O proxy está ativo, mas não foi possível descobrir o IP de saída agora (${ipErro}). Rode de novo em instantes.`
            : 'O proxy está ativo, mas o IP de saída não pôde ser confirmado nesta execução. Rode de novo em instantes.';
    }

    const CORES = {
        ok:      { borda: '#10b981', fundo: 'rgba(16,185,129,0.08)', texto: '#065f46' },
        alerta:  { borda: '#f59e0b', fundo: 'rgba(245,158,11,0.08)', texto: '#92400e' },
        critico: { borda: '#ef4444', fundo: 'rgba(239,68,68,0.08)',  texto: '#991b1b' },
        neutro:  { borda: '#6366f1', fundo: 'rgba(99,102,241,0.08)', texto: '#3730a3' },
    };
    const cor = CORES[nivel] || CORES.neutro;

    return (
        <>
            <div className="gb-section-title">Rota de saída (IP fixo)</div>
            <div
                className={`gb-egress-painel is-${nivel}`}
                style={{ border: `1px solid ${cor.borda}`, background: cor.fundo, borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span aria-hidden="true" style={{ fontSize: 18 }}>{icone}</span>
                    <strong style={{ color: cor.texto }}>{titulo}</strong>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '8px 0' }}>
                    <div>
                        <small style={{ display: 'block', opacity: 0.7 }}>IP de saída real</small>
                        <code style={{ fontSize: 15, fontWeight: 700 }}>{ipReal || '—'}</code>
                    </div>
                    <div>
                        <small style={{ display: 'block', opacity: 0.7 }}>IP do proxy configurado</small>
                        <code style={{ fontSize: 15 }}>{ipProxy || (configurado ? '—' : 'nenhum')}</code>
                    </div>
                    <div>
                        <small style={{ display: 'block', opacity: 0.7 }}>Proxy</small>
                        <code style={{ fontSize: 15 }}>
                            {configurado ? (ativo ? 'ativo' : 'inativo') : 'não configurado'}
                            {obrigatorio ? ' · obrigatório' : ''}
                        </code>
                    </div>
                </div>

                <p style={{ margin: '4px 0 0', color: cor.texto, fontSize: 13, lineHeight: 1.5 }}>{detalhe}</p>
            </div>
        </>
    );
}

// =============================================================================
// MODAL — DIAGNÓSTICO E ADOÇÃO DE TITULARES
// =============================================================================
/**
 * Responde à pergunta "por que o cartão que emiti no Web Banking não aparece?"
 * com fatos vindos do servidor, em vez de deixar o operador adivinhando.
 *
 * A causa quase sempre é autorização: um CorporateHolder criado pelo Web Banking
 * nasce sem conceder permissão ao Project da BLB, e a API não devolve cartão de
 * titular que ela não pode ver. "Adotar" concede essa permissão preservando as
 * que já existiam.
 */
function DiagnosticoModal({ environment, onClose, onAdotou }) {
    const [carregando, setCarregando] = useState(true);
    const [rel, setRel] = useState(null);
    const [adotando, setAdotando] = useState(false);
    const [resultado, setResultado] = useState(null);

    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const rodar = useCallback(async (signal) => {
        setCarregando(true);
        setResultado(null);
        try {
            const { data } = await api.get('/api/baas/account/diagnostico', { signal });
            if (aliveRef.current) setRel(data);
        } catch (e) {
            const msg = apiErr(e, 'Não foi possível executar o diagnóstico.');
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (aliveRef.current) setCarregando(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        rodar(controller.signal);
        return () => controller.abort();
    }, [rodar]);

    useEscapeKey(() => { if (!adotando) onClose(); });

    const pendentes = rel?.holders?.sem_permissao_do_project || 0;

    const adotar = async () => {
        if (adotando) return;
        setAdotando(true);
        try {
            const { data } = await api.post('/api/baas/account/adotar-holders', {});
            if (!aliveRef.current) return;
            setResultado(data);
            if (data?.adotados > 0) {
                toast.success(data.message || 'Titulares adotados.');
                invalidateCardsCache();
                onAdotou?.();
                await rodar();
            } else {
                toast.info(data?.message || 'Nada a adotar.');
            }
        } catch (e) {
            const msg = apiErr(e, 'Não foi possível adotar os titulares.');
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (aliveRef.current) setAdotando(false);
        }
    };

    return (
        <div className="gb-modal-overlay" onClick={() => { if (!adotando) onClose(); }}>
            <div
                className="gb-modal gb-modal--wide"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Diagnóstico de sincronização com a Stark Bank"
            >
                <div className="gb-modal-header gb-modal-header--dark">
                    <h3>
                        🔍 Diagnóstico de sincronização
                        {environment && (
                            <span className={`gb-env-badge ${environment === 'production' ? 'is-prod' : 'is-sbx'}`}>
                                ● {envLabelOf(environment)}
                            </span>
                        )}
                    </h3>
                    <button className="gb-modal-close" onClick={onClose} disabled={adotando} aria-label="Fechar">✕</button>
                </div>

                <div className="gb-modal-body">
                    {carregando ? (
                        <div className="gb-diag-loading">
                            <div className="gb-skel-line" />
                            <div className="gb-skel-line" />
                            <div className="gb-skel-line" />
                            <span>Consultando a Stark Bank...</span>
                        </div>
                    ) : !rel ? (
                        <div className="gb-diag-empty">
                            <p>Não foi possível obter o diagnóstico.</p>
                            <button type="button" className="gb-btn-cancel" onClick={() => rodar()}>Tentar de novo</button>
                        </div>
                    ) : (
                        <>
                            {/* Egress vem antes de tudo: se a saída está pelo IP
                                errado, essa é a causa raiz do invalidIp e o resto
                                do diagnóstico é ruído até resolver isso. */}
                            <EgressPainel egress={rel.egress} />

                            {/* A escada vem PRIMEIRO: se a conexão quebrou, os
                                números abaixo são todos zero e não significam
                                nada. Mostrar o erro cru da Stark é o que permite
                                agir sem abrir o log do servidor. */}
                            {asArray(rel.conexao?.etapas).length > 0 && (
                                <>
                                    <div className="gb-section-title">Teste de conectividade</div>
                                    <div className="gb-diag-escada">
                                        {asArray(rel.conexao.etapas).map((et) => (
                                            <div key={et.etapa} className={`gb-diag-step ${et.ok ? 'is-ok' : 'is-fail'}`}>
                                                <span className="gb-diag-step-mark">{et.ok ? '✓' : '✕'}</span>
                                                <span className="gb-diag-step-text">
                                                    <strong>{et.descricao}</strong>
                                                    {et.detalhe && <small>{et.detalhe}</small>}
                                                    {et.erro && (
                                                        <>
                                                            {et.origem_erro && (
                                                                <span className={`gb-diag-origem is-${et.origem_erro}`}>
                                                                    {et.origem_erro === 'sdk' ? 'defeito no pacote SDK — não é recusa da Stark'
                                                                        : et.origem_erro === 'stark' ? 'recusa da Stark Bank'
                                                                        : et.origem_erro === 'rede' ? 'falha de rede'
                                                                        : 'origem indeterminada'}
                                                                </span>
                                                            )}
                                                            <code>{et.erro}</code>
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {rel.conexao?.conclusao === 'sdk-incompativel' && rel.conexao?.sdk && (
                                <div className="gb-diag-sdk">
                                    <strong>Ambiente do servidor</strong>
                                    <div className="gb-diag-sdk-grid">
                                        <span>starkbank<b>{rel.conexao.sdk.starkbank || '—'}</b></span>
                                        <span>starkcore<b>{rel.conexao.sdk.starkcore || '—'}</b></span>
                                        <span>corporateHolder<b>{rel.conexao.sdk.tem_corporateHolder || '—'}</b></span>
                                        <span>corporateCard<b>{rel.conexao.sdk.tem_corporateCard || '—'}</b></span>
                                    </div>
                                    {asArray(rel.conexao.sdk.metodos_holder).length > 0 && (
                                        <p>Métodos em corporateHolder: <code>{asArray(rel.conexao.sdk.metodos_holder).join(', ')}</code></p>
                                    )}
                                </div>
                            )}

                            {asArray(rel.falhas).length > 0 && (
                                <div className="gb-diag-falhas">
                                    <strong>Erros retornados pela Stark Bank</strong>
                                    {asArray(rel.falhas).map((f, i) => (
                                        <p key={i}><b>{f.descricao}:</b> <code>{f.erro}</code></p>
                                    ))}
                                </div>
                            )}

                            <div className="gb-diag-nums">
                                <div className="gb-diag-num">
                                    <strong>{rel.holders?.total ?? 0}</strong>
                                    <small>titulares visíveis</small>
                                </div>
                                <div className={`gb-diag-num ${pendentes > 0 ? 'is-alert' : ''}`}>
                                    <strong>{pendentes}</strong>
                                    <small>sem permissão do Project</small>
                                </div>
                                <div className="gb-diag-num">
                                    <strong>{rel.cartoes?.na_stark ?? 0}</strong>
                                    <small>cartões na Stark</small>
                                </div>
                                <div className="gb-diag-num">
                                    <strong>{rel.cartoes?.no_espelho ?? 0}</strong>
                                    <small>no espelho local</small>
                                </div>
                            </div>

                            {asArray(rel.conclusoes).length > 0 && (
                                <div className="gb-diag-conclusoes">
                                    {asArray(rel.conclusoes).map((c, i) => (
                                        <p key={i}>• {c}</p>
                                    ))}
                                </div>
                            )}

                            {pendentes > 0 && rel.conexao?.conclusao !== 'credenciais' && rel.conexao?.conclusao !== 'corporate-indisponivel' && (
                                <div className="gb-diag-fix">
                                    <div>
                                        <strong>Causa provável encontrada.</strong>
                                        <p>
                                            Titular criado no Web Banking nasce sem conceder permissão ao Project da BLB —
                                            e a API não devolve cartão de titular que ela não pode ver. Adotar concede essa
                                            permissão <u>sem remover</u> os acessos que já existem.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="gb-btn-submit"
                                        onClick={adotar}
                                        disabled={adotando}
                                    >
                                        {adotando ? 'Adotando...' : `Adotar ${pendentes} titular${pendentes === 1 ? '' : 'es'}`}
                                    </button>
                                </div>
                            )}

                            {asArray(rel.holders?.lista).length > 0 && (
                                <>
                                    <div className="gb-section-title">Titulares</div>
                                    <div className="gb-diag-list">
                                        {asArray(rel.holders.lista).map((h) => (
                                            <div key={h.stark_holder_id} className="gb-diag-holder">
                                                <span className={`gb-diag-lock ${h.project_tem_permissao ? 'is-ok' : 'is-locked'}`}>
                                                    {h.project_tem_permissao ? '🔓' : '🔒'}
                                                </span>
                                                <span className="gb-diag-holder-name">{h.name || h.stark_holder_id}</span>
                                                <span className="gb-diag-holder-meta">
                                                    {h.project_tem_permissao ? 'visível pela API' : 'invisível pela API'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {resultado && asArray(resultado.resultados).length > 0 && (
                                <>
                                    <div className="gb-section-title">Resultado da adoção</div>
                                    <div className="gb-diag-list">
                                        {asArray(resultado.resultados).map((r, i) => (
                                            <div key={r.holderId || `res-${i}`} className="gb-diag-holder">
                                                <span className={`gb-diag-lock ${r.alterado ? 'is-ok' : 'is-locked'}`}>
                                                    {r.alterado ? '✓' : '✕'}
                                                </span>
                                                <span className="gb-diag-holder-name">{r.name || r.holderId}</span>
                                                <span className="gb-diag-holder-meta">
                                                    {r.alterado ? `adotado (via ${r.via})` : (r.erro || r.motivo || 'sem alteração')}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {asArray(rel.cartoes?.so_no_espelho).length > 0 && (
                                <div className="gb-diag-conclusoes">
                                    <p>
                                        <strong>Só no espelho local:</strong> {asArray(rel.cartoes.so_no_espelho).join(', ')}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="gb-modal-footer">
                    <button type="button" className="gb-btn-cancel" onClick={() => rodar()} disabled={carregando || adotando}>
                        ⟳ Rodar de novo
                    </button>
                    <button type="button" className="gb-btn-submit" onClick={onClose} disabled={adotando}>Fechar</button>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// EXPOSIÇÃO MENSAL — traduz o intervalo em risco comparável
// =============================================================================
/**
 * Por que existe: "R$ 1.000" diz pouco sem o intervalo, e o intervalo é fácil de
 * ignorar porque é texto pequeno. R$ 1.000 POR DIA e R$ 1.000 POR MÊS são coisas
 * separadas por um fator 30 — e o dropdown da Stark abre em "por dia", então o
 * valor perigoso é o padrão.
 *
 * A projeção é aproximada de propósito (mês comercial de 30 dias, 4,3 semanas) e
 * está rotulada como aproximada na tela. Serve para comparar titulares entre si e
 * para o operador notar um teto fora de escala, não para contabilidade.
 */
const FATOR_MENSAL = { day: 30, week: 4.3, month: 1, year: 1 / 12 };

const equivalenteMensal = (amount, interval) => {
    const v = Number(amount);
    if (!Number.isFinite(v) || v <= 0) return null;
    const f = FATOR_MENSAL[interval];
    // `instant` (por compra) e `lifetime` (sem renovar) não projetam em mês:
    // inventar um número aqui seria pior que não mostrar nada.
    if (!f) return null;
    return v * f;
};

/** Intervalo curto + cartão ativo = exposição que merece destaque na lista. */
const INTERVALOS_CURTOS = new Set(['instant', 'day']);

const nivelRisco = (h) => {
    if (h.sem_controle) return 2;                                   // nenhum limite
    if (INTERVALOS_CURTOS.has(h.intervalo_principal) && (h.cartoes?.ativos || 0) > 0) return 1;
    return 0;
};

// =============================================================================
// MODAL — GESTÃO DE TITULARES (onde o controle de gastos realmente mora)
// =============================================================================
/**
 * Por que esta tela existe: no modelo da Stark é o TITULAR que carrega o
 * controle de gastos, e o cartão herda. Até aqui o módulo só editava o cartão —
 * então um titular criado no Web Banking com "R$ 1.000 POR DIA" (o dropdown de
 * lá abre em "por dia") não tinha como ser corrigido pela plataforma. Ajustar
 * cartão por cartão não resolve: o teto do titular continua valendo.
 *
 * Cada linha mostra o que decide alguma coisa:
 *   • o limite e o intervalo em vigor, ou o alerta de "sem controle"
 *   • quantos cartões dependem daquele titular, por status
 *   • se o Project da BLB tem permissão de gerenciar (o 🔒 que nos custou horas)
 */
function GestaoTitularesModal({ environment, onClose, onAlterado }) {
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [holders, setHolders] = useState([]);
    const [busca, setBusca] = useState('');
    const [editando, setEditando] = useState(null);   // stark_holder_id

    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const carregar = useCallback(async (signal) => {
        setCarregando(true);
        setErro(null);
        try {
            const { data } = await api.get('/api/baas/account/holders/gestao', { signal });
            if (aliveRef.current) setHolders(asArray(data?.holders));
        } catch (e) {
            const msg = apiErr(e, 'Não foi possível carregar os titulares.');
            if (msg && aliveRef.current) setErro(msg);
        } finally {
            if (aliveRef.current) setCarregando(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        carregar(controller.signal);
        return () => controller.abort();
    }, [carregar]);

    useEscapeKey(() => { if (!editando) onClose(); });

    const term = normalize(useDebounced(busca, SEARCH_DEBOUNCE));

    /**
     * Ordena por RISCO, não por nome: quem está sem controle vem primeiro, depois
     * quem tem limite diário com cartão ativo, e só então o resto em ordem
     * alfabética. Numa lista de 30 titulares, o que precisa de atenção não pode
     * depender de rolagem.
     */
    const filtrados = useMemo(() => {
        const base = term
            ? holders.filter((h) => normalize(`${h.name} ${h.stark_holder_id} ${h.status}`).includes(term))
            : holders;
        return [...base].sort((a, b) => {
            const dr = nivelRisco(b) - nivelRisco(a);
            if (dr !== 0) return dr;
            const dc = (b.cartoes?.total || 0) - (a.cartoes?.total || 0);
            if (dc !== 0) return dc;
            return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR');
        });
    }, [holders, term]);

    const semControle = useMemo(() => holders.filter((h) => h.sem_controle).length, [holders]);

    const resumo = useMemo(() => {
        let cartoes = 0;
        let ativos = 0;
        let exposicao = 0;
        let semProjecao = 0;
        let diarios = 0;

        for (const h of holders) {
            cartoes += h.cartoes?.total || 0;
            ativos += h.cartoes?.ativos || 0;
            if (INTERVALOS_CURTOS.has(h.intervalo_principal)) diarios += 1;
            const eq = equivalenteMensal(h.limite_principal, h.intervalo_principal);
            if (eq == null) { if (!h.sem_controle) semProjecao += 1; }
            else exposicao += eq;
        }
        return { cartoes, ativos, exposicao, semProjecao, diarios };
    }, [holders]);

    const aoSalvar = useCallback(async () => {
        setEditando(null);
        await carregar();
        // A lista de cartões mostra limite herdado: mudou o titular, mudou o card.
        invalidateCardsCache();
        onAlterado?.();
    }, [carregar, onAlterado]);

    return (
        <div className="gb-modal-overlay" onClick={() => { if (!editando) onClose(); }}>
            <div
                className="gb-modal gb-modal--wide"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Gestão de titulares"
            >
                <div className="gb-modal-header gb-modal-header--dark">
                    <h3>
                        👤 Titulares e controle de gastos
                        {environment && (
                            <span className={`gb-env-badge ${environment === 'production' ? 'is-prod' : 'is-sbx'}`}>
                                ● {envLabelOf(environment)}
                            </span>
                        )}
                    </h3>
                    <button className="gb-modal-close" onClick={onClose} aria-label="Fechar">✕</button>
                </div>

                <div className="gb-modal-body">
                    <div className="gb-tit-explica">
                        <strong>O limite mora no titular.</strong> Um cartão sem regra própria herda o controle do
                        titular dele — então corrigir aqui vale para todos os cartões daquele titular de uma vez.
                    </div>

                    {!carregando && !erro && holders.length > 0 && (
                        <div className="gb-tit-resumo">
                            <div className="gb-tit-resumo-item">
                                <strong>{holders.length}</strong>
                                <small>{plural(holders.length, 'titular', 'titulares')}</small>
                            </div>
                            <div className="gb-tit-resumo-item">
                                <strong>{resumo.cartoes}</strong>
                                <small>{plural(resumo.cartoes, 'cartão', 'cartões')} · {resumo.ativos} {plural(resumo.ativos, 'ativo', 'ativos')}</small>
                            </div>
                            <div className="gb-tit-resumo-item is-destaque">
                                <strong>{resumo.exposicao > 0 ? fmtBRL(resumo.exposicao) : '—'}</strong>
                                <small>
                                    teto mensal somado (aprox.)
                                    {resumo.semProjecao > 0 && ` · ${resumo.semProjecao} sem projeção`}
                                </small>
                            </div>
                        </div>
                    )}

                    {resumo.diarios > 0 && (
                        <div className="gb-tit-alerta">
                            <strong>
                                {resumo.diarios} {plural(resumo.diarios, 'titular', 'titulares')} com limite de renovação diária.
                            </strong>
                            <span>
                                {' '}Um teto por dia se renova todo dia — R$ 1.000/dia equivale a cerca de R$ 30.000/mês.
                                Se a intenção era um limite mensal, ajuste o intervalo.
                            </span>
                        </div>
                    )}

                    {semControle > 0 && (
                        <div className="gb-tit-alerta">
                            <strong>
                                {semControle} {plural(semControle, 'titular', 'titulares')} sem controle de gastos.
                            </strong>
                            <span> Sem regra, os cartões {plural(semControle, 'dele', 'deles')} podem gastar até o saldo total da empresa.</span>
                        </div>
                    )}

                    {holders.length > 3 && (
                        <input
                            type="search"
                            className="gb-pais-busca"
                            placeholder="Buscar titular por nome..."
                            value={busca}
                            onChange={(e) => setBusca(e.target.value.slice(0, 60))}
                        />
                    )}

                    {carregando ? (
                        <div className="gb-diag-loading">
                            <div className="gb-skel-line" />
                            <div className="gb-skel-line" />
                            <div className="gb-skel-line" />
                            <span>Lendo titulares, regras e cartões na Stark Bank...</span>
                        </div>
                    ) : erro ? (
                        <div className="gb-diag-empty">
                            <p>{erro}</p>
                            <button type="button" className="gb-btn-cancel" onClick={() => carregar()}>Tentar de novo</button>
                        </div>
                    ) : filtrados.length === 0 ? (
                        <div className="gb-diag-empty">
                            <p>{term ? `Nenhum titular encontrado para “${busca.trim()}”.` : 'Nenhum titular nesta conta.'}</p>
                        </div>
                    ) : (
                        <div className="gb-tit-lista">
                            {filtrados.map((h) => (
                                <TitularLinha
                                    key={h.stark_holder_id}
                                    holder={h}
                                    emEdicao={editando === h.stark_holder_id}
                                    onEditar={() => setEditando(h.stark_holder_id)}
                                    onCancelarEdicao={() => setEditando(null)}
                                    onSalvo={aoSalvar}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="gb-modal-footer">
                    <button type="button" className="gb-btn-cancel" onClick={() => carregar()} disabled={carregando || !!editando}>
                        ⟳ Recarregar
                    </button>
                    <button type="button" className="gb-btn-submit" onClick={onClose} disabled={!!editando}>Fechar</button>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// LINHA DO TITULAR
// =============================================================================
function TitularLinha({ holder, emEdicao, onEditar, onCancelarEdicao, onSalvo }) {
    const c = holder.cartoes || { total: 0, ativos: 0, bloqueados: 0, pendentes: 0 };
    const meta = statusMeta(holder.status);
    const intervalo = INTERVAL_LABEL_FULL[holder.intervalo_principal] || holder.intervalo_principal || '';
    const eqMensal = equivalenteMensal(holder.limite_principal, holder.intervalo_principal);
    const curto = INTERVALOS_CURTOS.has(holder.intervalo_principal) && (c.ativos || 0) > 0;

    return (
        <div className={`gb-tit-item ${holder.sem_controle ? 'is-alerta' : ''} ${curto ? 'is-curto' : ''} ${emEdicao ? 'is-editando' : ''}`}>
            <div className="gb-tit-topo">
                <div className="gb-tit-ident">
                    <span className="gb-tit-avatar" aria-hidden="true">
                        {(holder.name || '?').charAt(0).toUpperCase()}
                    </span>
                    <div className="gb-tit-nome">
                        <strong>{holder.name || holder.stark_holder_id}</strong>
                        <small>
                            <span className="gb-status-pill" style={{ background: `${meta.color}1f`, color: meta.color }}>
                                {meta.label}
                            </span>
                            {/* O 🔒 é a diferença entre "existe" e "gerenciável por API". */}
                            {holder.project_tem_permissao === false && (
                                <span className="gb-tit-lock" title="O Project da BLB não tem permissão sobre este titular — use Adotar titulares no Diagnóstico">
                                    🔒 sem permissão
                                </span>
                            )}
                            {holder.permissoes_indisponiveis && (
                                <span className="gb-tit-lock is-neutro" title="Não foi possível auditar as permissões neste ambiente">
                                    permissão não auditada
                                </span>
                            )}
                        </small>
                    </div>
                </div>

                <div className="gb-tit-controle">
                    {holder.sem_controle ? (
                        <span className="gb-tit-semcontrole">⚠️ sem controle de gastos</span>
                    ) : (
                        <>
                            <strong>{fmtBRL(holder.limite_principal)}</strong>
                            <small className={curto ? 'is-curto' : ''}>{intervalo}</small>
                            {/* Só mostra a projeção quando o intervalo NÃO é mensal:
                                repetir "≈ R$ 1.000/mês" embaixo de "por mês" é ruído. */}
                            {eqMensal != null && holder.intervalo_principal !== 'month' && (
                                <small className="gb-tit-eq">≈ {fmtBRL(eqMensal)}/mês</small>
                            )}
                        </>
                    )}
                </div>

                <div
                    className={`gb-tit-cartoes ${c.total === 0 ? 'is-vazio' : ''}`}
                    title={`${c.ativos} ativos · ${c.pendentes} pendentes · ${c.bloqueados} bloqueados · ${c.cancelados || 0} cancelados`}
                >
                    <strong>{c.total}</strong>
                    <small>{plural(c.total, 'cartão', 'cartões')}</small>
                    {c.pendentes > 0 && <em>{c.pendentes} pend.</em>}
                </div>

                {!emEdicao && (
                    <button type="button" className="gb-btn-cancel gb-tit-btn" onClick={onEditar}>
                        {holder.sem_controle ? 'Definir limite' : 'Editar controle'}
                    </button>
                )}
            </div>

            {emEdicao && (
                <TitularEditor
                    holder={holder}
                    onCancelar={onCancelarEdicao}
                    onSalvo={onSalvo}
                />
            )}
        </div>
    );
}

// =============================================================================
// EDITOR DO TITULAR — limite, nome, bloqueio e cancelamento em cascata
// =============================================================================
function TitularEditor({ holder, onCancelar, onSalvo }) {
    const primeira = asArray(holder.rules)[0] || null;

    const [limite, setLimite] = useState(primeira?.amount != null ? String(primeira.amount) : '');
    const [interval, setInterval_] = useState(primeira?.interval || 'month');
    const [nome, setNome] = useState(holder.name || '');
    // Janela de horário do TITULAR: vale para todos os cartões dele. A Stark
    // guarda o schedule no CorporateRule tanto no cartão quanto no titular —
    // então aqui editamos a mesma frase que o cartão já usa, via ScheduleBuilder.
    const [schedule, setSchedule] = useState(primeira?.schedule || '');
    const [salvando, setSalvando] = useState(false);
    const [confirmandoCancel, setConfirmandoCancel] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    const { catalogos } = useRuleCatalogs();
    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const id = holder.stark_holder_id;
    const cartoesVivos = (holder.cartoes?.total || 0) - (holder.cartoes?.cancelados || 0);

    const salvarRegra = async () => {
        if (salvando) return;
        const valor = parseLimite(limite);
        if (valor === null || valor <= 0) {
            toast.error('Informe um limite maior que zero para o titular.');
            return;
        }

        setSalvando(true);
        try {
            // ⚠️ A API SUBSTITUI o conjunto de regras do titular. Preservamos os
            // filtros da regra existente (categorias, países, métodos) para que
            // ajustar o VALOR não apague silenciosamente as restrições que alguém
            // configurou antes.
            //
            // ⚠️ O `schedule` é a EXCEÇÃO: ele é editável aqui (via ScheduleBuilder),
            // então usamos o valor do state — não o cru da regra original. String
            // vazia significa "sem janela de horário" e é omitida do payload, o que
            // remove a janela na Stark (comportamento desejado: limpar volta a
            // liberar qualquer horário).
            const regra = {
                name: primeira?.name || 'Limite do titular',
                amount: valor,
                interval,
                ...(asArray(primeira?.categories).length ? { categories: primeira.categories } : {}),
                ...(asArray(primeira?.countries).length ? { countries: primeira.countries } : {}),
                ...(asArray(primeira?.methods).length ? { methods: primeira.methods } : {}),
                ...(asArray(primeira?.purposes).length ? { purposes: primeira.purposes } : {}),
                ...(schedule.trim() ? { schedule: schedule.trim() } : {}),
            };
            await api.patch(`/api/baas/account/holders/${encodeURIComponent(id)}/rules`, { rules: [regra] });

            if (nome.trim() && nome.trim() !== holder.name) {
                await api.patch(`/api/baas/account/holders/${encodeURIComponent(id)}`, { name: nome.trim() });
            }

            toast.success('Controle de gastos do titular atualizado. Vale para todos os cartões dele.');
            onSalvo();
        } catch (e) {
            const msg = apiErr(e, 'Não foi possível salvar o controle do titular.');
            if (msg && aliveRef.current) toast.error(msg);
            if (aliveRef.current) setSalvando(false);
        }
    };

    const alterarStatus = async (status) => {
        if (salvando) return;
        setSalvando(true);
        try {
            await api.patch(`/api/baas/account/holders/${encodeURIComponent(id)}`, { status });
            toast.success(status === 'blocked'
                ? 'Titular bloqueado. Os cartões dele param de autorizar.'
                : 'Titular reativado.');
            onSalvo();
        } catch (e) {
            const msg = apiErr(e, 'Não foi possível alterar o status do titular.');
            if (msg && aliveRef.current) toast.error(msg);
            if (aliveRef.current) setSalvando(false);
        }
    };

    const cancelarTitular = async () => {
        if (salvando) return;
        setSalvando(true);
        try {
            const { data } = await api.post(
                `/api/baas/account/holders/${encodeURIComponent(id)}/cancelar`,
                { confirmar: 'CANCELAR' },
            );
            toast.success(data?.message || 'Titular cancelado.');
            onSalvo();
        } catch (e) {
            const msg = apiErr(e, 'Não foi possível cancelar o titular.');
            if (msg && aliveRef.current) toast.error(msg);
            if (aliveRef.current) setSalvando(false);
        }
    };

    // Filtros que continuam sendo apenas PRESERVADOS aqui (não editáveis neste
    // editor): categorias, países, métodos e propósitos. O `schedule` saiu desta
    // lista de propósito — ele passou a ser editável logo abaixo.
    const temFiltros = asArray(primeira?.categories).length
        || asArray(primeira?.countries).length
        || asArray(primeira?.methods).length
        || asArray(primeira?.purposes).length;

    return (
        <div className="gb-tit-editor">
            <div className="gb-form-row">
                <div className="gb-form-field">
                    <label htmlFor={`gb-tit-nome-${id}`}>Nome do titular</label>
                    <input
                        id={`gb-tit-nome-${id}`}
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        maxLength={120}
                        disabled={salvando}
                    />
                </div>
                <div className="gb-form-field">
                    <label htmlFor={`gb-tit-limite-${id}`}>Limite (R$)</label>
                    <input
                        id={`gb-tit-limite-${id}`}
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={LIMITE_MAX}
                        value={limite}
                        onChange={(e) => setLimite(e.target.value)}
                        disabled={salvando}
                        placeholder="obrigatório"
                    />
                </div>
                <div className="gb-form-field">
                    <label htmlFor={`gb-tit-interval-${id}`}>Renova a cada</label>
                    <select
                        id={`gb-tit-interval-${id}`}
                        value={interval}
                        onChange={(e) => setInterval_(e.target.value)}
                        disabled={salvando}
                    >
                        {asArray(catalogos?.intervals).map((i) => (
                            <option key={i.code} value={i.code}>{i.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Aviso concreto do caso real: "por dia" num titular de frota. */}
            {interval === 'day' && (
                <p className="gb-field-hint gb-field-hint--aviso">
                    <strong>Confirme o intervalo.</strong> "Por dia" significa que este teto se renova
                    todo dia — {fmtBRL(parseLimite(limite) || 0)} por dia equivale a
                    cerca de {fmtBRL((parseLimite(limite) || 0) * 30)} por mês.
                </p>
            )}

            {temFiltros && (
                <p className="gb-field-hint">
                    Este titular tem restrições de uso configuradas (categorias, países ou formas de pagamento).
                    Elas são <strong>preservadas</strong> ao salvar — aqui mudam o valor, o intervalo e a janela de horário.
                </p>
            )}

            {/* Janela de horário do titular — vale para todos os cartões dele.
                Mesmo componente e mesmo formato de frase usados no cartão; a Stark
                aceita `schedule` no CorporateRule do titular do mesmo jeito. */}
            <div className="gb-section-title">Quando os cartões deste titular podem passar</div>
            <ScheduleBuilder value={schedule} onChange={setSchedule} disabled={salvando} />

            <div className="gb-tit-editor-acoes">
                <div className="gb-tit-editor-esq">
                    {holder.status === 'active' && (
                        <button type="button" className="gb-action-btn gb-action-orange" onClick={() => alterarStatus('blocked')} disabled={salvando}>
                            🔒 Bloquear titular
                        </button>
                    )}
                    {holder.status === 'blocked' && (
                        <button type="button" className="gb-action-btn gb-action-green" onClick={() => alterarStatus('active')} disabled={salvando}>
                            🔓 Reativar titular
                        </button>
                    )}
                    {holder.status !== 'canceled' && (
                        <button type="button" className="gb-action-btn gb-action-red" onClick={() => setConfirmandoCancel(true)} disabled={salvando}>
                            ✕ Cancelar titular
                        </button>
                    )}
                </div>
                <div className="gb-tit-editor-dir">
                    <button type="button" className="gb-btn-cancel" onClick={onCancelar} disabled={salvando}>Cancelar</button>
                    <button type="button" className="gb-btn-submit" onClick={salvarRegra} disabled={salvando}>
                        {salvando ? 'Salvando...' : '💾 Salvar controle'}
                    </button>
                </div>
            </div>

            <p className="gb-field-hint">
                <strong>Bloquear o titular</strong> suspende todos os cartões dele de uma vez, e é reversível.
            </p>

            {confirmandoCancel && (
                <div className="gb-cancel-warning">
                    <strong>Cancelar o titular cancela TODOS os cartões dele.</strong>
                    <p>
                        {cartoesVivos > 0 ? (
                            <>
                                Este titular tem <strong>{cartoesVivos} {plural(cartoesVivos, 'cartão', 'cartões')} em uso</strong>.
                                {' '}Eles serão cancelados junto, de forma permanente — se algum estiver na mão de um
                                motorista agora, ele para de funcionar na hora.
                            </>
                        ) : (
                            <>Este titular não tem cartões ativos. A ação é permanente.</>
                        )}
                    </p>
                    <p>Digite <strong>CANCELAR</strong> para confirmar.</p>
                    <div className="gb-form-field">
                        <input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="CANCELAR"
                            autoComplete="off"
                            disabled={salvando}
                        />
                    </div>
                    <div className="gb-form-actions">
                        <button type="button" className="gb-btn-cancel" onClick={() => { setConfirmandoCancel(false); setConfirmText(''); }} disabled={salvando}>
                            Voltar
                        </button>
                        <button
                            type="button"
                            className="gb-btn-red"
                            disabled={confirmText.trim().toUpperCase() !== 'CANCELAR' || salvando}
                            onClick={cancelarTitular}
                        >
                            {salvando ? 'Cancelando...' : `Cancelar titular e ${cartoesVivos} ${plural(cartoesVivos, 'cartão', 'cartões')}`}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}